import { sendActivityHeartbeat } from './services/activityService';
import { updateWidgetData } from './services/widgetDataService';
import { worthMilestoneSteps, recentPace } from './components/wealth/WealthPaths'; // w2_09
import { planGrowth } from './components/moneyPlan/MoneyPlan'; // w2_09
import { computeBudgetSpentPercent } from './utils/budgetMath';
import { buildFundsWidgetData } from './utils/fundMath';
import { getNetWorthFunds } from './utils/netWorthMath';
import { buildBillsWidgetData } from './utils/recurringMath';
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useSwipeBack } from "./utils/swipeBack"; // w2_21
import { Capacitor, registerPlugin } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { App as CapApp } from "@capacitor/app";
import { reviewService } from "@/services/reviewService";
import { canShowPopup, markPopupSpacing } from "@/utils/popupSpacing";
import ReviewAskScreen from "@/components/review/ReviewAskScreen";
import UpdatePromptSheet from "@/components/review/UpdatePromptSheet";
import { checkForUpdate, markUpdatePromptShown, openAppStorePage } from "@/utils/updatePrompt";
const AppleSignIn = registerPlugin("AppleSignIn");
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { customToast } from "./utils/customToast";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Calendar, 
  Target, 
  Sparkles,
  ChevronLeft,
  Flame,
  Award,
  HelpCircle,
  PiggyBank,
  ShoppingBag,
  Plus,
  Trash2,
  GripVertical,
  Save,
  Check,
  DollarSign,
  X,
  ScrollText,
  Quote,
  Wallet,
  Edit3,
  Tag,
  TrendingDown,
  CircleDollarSign,
  User,
  TrendingUp,
  BarChart3,
  Landmark,
  CreditCard,
  Building2,
  Banknote,
  LogOut,
  Settings,
  Upload,
  Image,
  Link as LinkIcon,
  ExternalLink,
  Minus,
  ZoomIn,
  RotateCcw,
  ChevronDown,
  Info,
  Calculator,
  AlertCircle,
  Search,
  FileText,
  Home,
  Palette,
  Receipt,
  RefreshCw,
  ArrowRightLeft,
  ArrowDown,
  PieChart,
  CalendarDays,
  Download
} from "lucide-react";
import { Progress } from "./components/ui/progress";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import { Checkbox } from "./components/ui/checkbox";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Slider } from "./components/ui/slider";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";

// Import utility functions
import { formatCurrency, formatCurrencyWhole, formatCurrencyShort, setCurrentCurrency, CURRENCIES } from "./utils/formatters";
import { getAccentColors, cardColorMap, defaultCategories, getThemeColors, fireConfetti } from "./utils/themes";
import { getMonthKey, getDaysInMonth, getFirstDayOfMonth, isToday, isFutureDay } from "./utils/dateHelpers";
import { THEME_COLORS, DEFAULT_SINKING_FUNDS, DEFAULT_INVESTMENT_ACCOUNTS } from "./utils/constants";

// Import extracted components - Common
import TabNav from "./components/common/TabNav";
import StatsCard from "./components/common/StatsCard";
import QuizBox from "./components/common/QuizBox";
import ProfilePicture from "./components/common/ProfilePicture";
import LoadingScreen from "./components/common/LoadingScreen";

// Import extracted components - Other
import DayCell from "./components/calendar/DayCell";
import CustomDataLabel from "./components/charts/CustomDataLabel";

// Import extracted components - Onboarding
import OnboardingFlow from "./components/onboarding/OnboardingFlow";
import WelcomeTutorial, { TUTORIAL_SLIDES } from "./components/onboarding/WelcomeTutorial"; // w2_05
import { W2LoginPage, W2NameEntryPage } from "./components/onboarding/Worthly2Welcome"; // w2_05

// Import extracted components - Tabs
import HomeTab from "./components/tabs/HomeTab";
import { OPEN_MONEY_CLUB_EVENT } from "./components/community/MoneyClub";
import RulesTab from "./components/tabs/RulesTab";
import WishlistTab from "./components/tabs/WishlistTab";
import CalendarTab from "./components/tabs/CalendarTab";
import SinkingFundsTab from "./components/tabs/SinkingFundsTab";
import { investmentBalance, localDay, monthEndDay } from "./utils/investMath"; // dark_13: one sum for investment balances
import BudgetTab from "./components/tabs/BudgetTab";
import PlansTab from "./components/tabs/PlansTab";
import NotesTab from "./components/tabs/NotesTab";
import FutureWorthTab from "./components/tabs/FutureWorthTab";
import YearlyCosts from "./components/plans/YearlyCosts"; // w2_16
import DebtPayoff from "./components/plans/DebtPayoff"; // w2_17
import MoneyPlanPage from "./components/moneyPlan/MoneyPlanPage"; // mp_01

// Import extracted components - Settings
// Lazy load SettingsDialog for better performance
const SettingsDialog = lazy(() => import("./components/settings/SettingsDialog"));

// Import subscription context and premium components
import { SubscriptionProvider, useSubscription } from "./contexts/SubscriptionContext";
import { useDarkScope } from './utils/darkMode'; // dark_01, dark_02
import { NotificationProvider, useNotifications } from "./contexts/NotificationContext";
import NotificationPermissionModal from "./components/common/NotificationPermissionModal";
import { TrialPrompt, PaywallModal, PremiumFeature, PremiumBadge, PremiumFeaturesModal } from "./components/premium/PremiumComponents";
import TourController from "./components/Tour/TourController";
import { getTourState } from "./utils/tourState";
import { setBadge } from "./services/badgeService";
import { countDueNow, syncReminderNotifications } from "./utils/reminderNotifications";
import { syncMonthEndNotification } from "./utils/monthEndNotification";

import { keychainService } from './services/keychainService';
import { SiriHowToHost } from './components/siri/SiriHowTo'; // reorder_02
import Worthly2Intro from './components/onboarding/Worthly2Intro'; // w2_02
import { PURPLE_TOGGLE_EVENT, isPurpleFlagSet, applyPurple, usePurple } from './utils/purple';
import { PLAN_TOGGLE_EVENT, isPlanFlagSet, setPlanFlag, applyPlan, isPlanOn, isMoneyPlanShown } from './utils/plan'; // plan_01, mp_01, w2_01, w2_09
import { setFutureIncomeRule, countsInProjection, signOf } from './utils/futureIncome'; // plan_25, plan_28
import { AppIcon } from './utils/appIcon';
// API URL from environment
const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://worthly-backend-production.up.railway.app';

// Note: Most components have been extracted to separate files
// See /utils/ for helper functions and /components/ for reusable components


// ============== Onboarding Components ==============
// OnboardingFlow and WelcomeTutorial have been extracted to:
// - /components/onboarding/OnboardingFlow.jsx
// - /components/onboarding/WelcomeTutorial.jsx


// ============== Backend Outage Detection ==============
// Distinguishes a REAL outage (Railway edge fallback) from a normal
// FastAPI 404. Railway's fallback sets the `x-railway-fallback` header.
// A plain {"detail":"Not Found"} from FastAPI must NOT trigger the screen.
const isRailwayFallback = (res) => {
  try {
    if (!res) return false;
    if (res.headers && res.headers.get && res.headers.get('x-railway-fallback')) return true;
    // 502/503 from the edge also indicate the service isn't reachable
    if (res.status === 502 || res.status === 503) return true;
    return false;
  } catch (e) {
    return false;
  }
};

// ============== Service Down Screen ==============
const ServiceDownScreen = ({ onRetry, preview = false }) => {
  const [checking, setChecking] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [countdown, setCountdown] = useState(30);

  // Auto-retry: silently ping health every 30s; reload on recovery.
  useEffect(() => {
    const tick = setInterval(() => setCountdown((c) => (c <= 1 ? 30 : c - 1)), 1000);
    const poll = setInterval(async () => {
      if (preview) return; // preview mode: never auto-recover
      try {
        const res = await fetch(`${API_URL}/api/health`);
        if (res.ok && !isRailwayFallback(res)) {
          window.location.reload();
        }
      } catch (e) { /* still down, keep waiting */ }
    }, 30000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, []);

  const handleRetry = async () => {
    setChecking(true);
    if (preview) {
      // preview mode: simulate a failed retry so the email state shows
      setTimeout(() => { setChecking(false); setAttempts((a) => a + 1); }, 900);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/health`);
      if (res.ok && !isRailwayFallback(res)) {
        if (onRetry) onRetry();
        return;
      }
    } catch (e) { /* still down */ }
    setChecking(false);
    setAttempts((a) => a + 1);
  };

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center text-center px-8"
      style={{
        background: `
          radial-gradient(circle at 20% 12%, #f9c5d8 0%, transparent 45%),
          radial-gradient(circle at 85% 18%, #e8d4f0 0%, transparent 40%),
          radial-gradient(circle at 50% 55%, #d6dcf5 0%, transparent 50%),
          radial-gradient(circle at 80% 85%, #cfe3f0 0%, transparent 45%),
          linear-gradient(160deg, #f7cadb 0%, #e3d5f2 35%, #d3dcf6 65%, #d4e6ef 100%)`,
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 72px)',
      }}
    >
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-[340px]">
        <img
          src="/logotransparent.png"
          alt="Worthly"
          className="w-28 h-28 mb-9"
        />

        <div className="flex gap-2 mb-7">
          <span className="w-2 h-2 rounded-full" style={{ background: '#c98fc4', animation: 'worthlyPulse 1.4s ease-in-out infinite' }} />
          <span className="w-2 h-2 rounded-full" style={{ background: '#9b8fd6', animation: 'worthlyPulse 1.4s ease-in-out 0.2s infinite' }} />
          <span className="w-2 h-2 rounded-full" style={{ background: '#7fa8d4', animation: 'worthlyPulse 1.4s ease-in-out 0.4s infinite' }} />
        </div>

        <h1
          className="mb-5"
          style={{ fontFamily: "'DM Serif Display', serif", fontSize: '40px', lineHeight: 1.05, color: '#1A1A1F', letterSpacing: '-0.5px' }}
        >
          We'll be<br />right back.
        </h1>

        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '17px', lineHeight: 1.5, color: '#4b465f', maxWidth: '300px', marginBottom: '34px' }}>
          We're having a brief service hiccup on our end. This isn't your connection or your data - we're already on it, and you'll be back to your money in just a moment.
        </p>
      </div>

      <div className="w-full max-w-[340px]">
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#8a85a0', marginBottom: '14px', height: '16px' }}>
          {checking ? 'Checking\u2026' : `Checking again in ${countdown}s\u2026`}
        </div>
        <button
          onClick={handleRetry}
          disabled={checking}
          className="w-full flex items-center justify-center gap-2.5 rounded-full"
          style={{ background: '#1A1A1F', color: '#fff', border: 'none', padding: '22px', fontFamily: "'DM Sans', sans-serif", fontSize: '19px', fontWeight: 700, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', opacity: checking ? 0.6 : 1 }}
        >
          {checking ? 'Checking\u2026' : 'Try again'}
        </button>
        {attempts >= 1 && (
          <div style={{ fontFamily: "'DM Sans', sans-serif", marginTop: '22px', fontSize: '17px', lineHeight: 1.5, color: '#6b6680' }}>
            Hmm, still no luck? Send us a message at <a href="mailto:hello@worthlyhq.com" style={{ color: '#8b5fbf', textDecoration: 'underline' }}>hello@worthlyhq.com</a> - we'd be happy to help 💛
          </div>
        )}
      </div>

      <style>{`@keyframes worthlyPulse { 0%,100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.15); } }`}</style>
    </div>
  );
};

// ============== Auth Context ==============
const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backendDown, setBackendDown] = useState(false);
  const [devForceDown, setDevForceDown] = useState(false);
  const [userData, setUserData] = useState(null);
  const [appSettings, setAppSettings] = useState(null);
  const [theme, setTheme] = useState('pink'); // Default theme
  // plan_25: next year's future income in projections, from your settings
  setFutureIncomeRule(!!(userData && userData.budget_settings && userData.budget_settings.future_income_this_year_only));
  const location = useLocation();
  const navigate = useNavigate();

  // Apply theme to document

  // Send activity heartbeat on app open (for timezone + inactivity tracking)
  useEffect(() => {
    if (user?.user_id) {
      sendActivityHeartbeat();
    }
  }, [user?.user_id]);
  useEffect(() => {
    // Pink, Blue and Warm are the only themes (Neutral was removed in purple_01).
    const saved = userData?.theme || 'pink';
    const currentTheme = ['pink', 'blue', 'warm'].includes(saved) ? saved : 'pink';
    setTheme(currentTheme);
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [userData?.theme]);

  // plan_01: Money plan (admin-only), layered on top of Pink, flag kept on this phone.
  const [planFlag, setPlanFlagState] = useState(isPlanFlagSet);
  useEffect(() => {
    const onPlanToggle = () => setPlanFlagState(isPlanFlagSet());
    window.addEventListener(PLAN_TOGGLE_EVENT, onPlanToggle);
    return () => window.removeEventListener(PLAN_TOGGLE_EVENT, onPlanToggle);
  }, []);
  // w2_01: Worthly 2.0 is saved on your account as worthly2. The old switch on
  // this phone moves you across once, then it's switched off for good.
  const hasUserData = !!userData;
  useEffect(() => {
    if (hasUserData && planFlag && userData.worthly2 === undefined) {
      saveUserData({ worthly2: true, theme: 'pink' });
      setPlanFlag(false);
    }
  }, [hasUserData, planFlag, userData?.worthly2]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    applyPlan((userData?.worthly2 === true || planFlag) && (userData?.theme || 'pink') === 'pink');
  }, [planFlag, userData?.worthly2, userData?.theme]);
  // dark_02: dark mode on 2.0 screens - Worthly 2.0, or welcome/sign-up/setup (2.0 for everyone)
  useDarkScope(
    ((userData?.worthly2 === true || planFlag) && (userData?.theme || 'pink') === 'pink')
      || (!loading && (!user || (!!userData && !userData.onboarding_complete))),
    !loading || !!userData,
  );

  // Purple (admin-only): layered on top of Pink, flag kept on this phone.
  const [purpleFlag, setPurpleFlagState] = useState(isPurpleFlagSet);
  useEffect(() => {
    const onToggle = () => setPurpleFlagState(isPurpleFlagSet());
    window.addEventListener(PURPLE_TOGGLE_EVENT, onToggle);
    return () => window.removeEventListener(PURPLE_TOGGLE_EVENT, onToggle);
  }, []);
  useEffect(() => {
    // The DEVELOPER switch also brings the diary-style tab bar (second arg).
    // plan_01: Purple stays off while Money plan is on.
    const on = purpleFlag && !planFlag && (userData?.theme || 'pink') === 'pink';
    applyPurple(on, on);
  }, [purpleFlag, planFlag, userData?.theme]);

  // Define functions first before useEffects that use them

  // Authenticated fetch: fresh Keychain read + centralized 401 handling.
  // Fixes the bug where getCached() returned stale/null token silently, and
  // where 401 responses left the app in a broken state until force-quit.
  const authedFetch = async (url, options = {}) => {
    const token = await keychainService.getItem('session_token');
    const response = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        'Authorization': `Bearer ${token}`,
      }
    });
    if (response.status === 401) {
      console.warn('[Auth] 401 from backend, clearing session and bouncing to login');
      await keychainService.removeItem('session_token');
      setUser(null);
      setUserData(null);
      navigate('/');
    }
    return response;
  };

  const loadUserData = async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/user/data`);
      if (response.ok) {
        const data = await response.json();
        setUserData(data);
        if (data.currency) setCurrentCurrency(data.currency);
        
        return data;
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
    return null;
  };

  const loadAppSettings = async () => {
    try {
      const response = await authedFetch(`${API_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setAppSettings(data);
        return data;
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
    return null;
  };

  const saveUserData = async (newData) => {
    try {
      const response = await authedFetch(`${API_URL}/api/user/data`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newData)
      });
      if (response.ok) {
        // mp_01: a dotted name like 'money_diary.plan' updates just that
        // part, the same way the server saves it.
        setUserData(prev => {
          const next = { ...prev };
          Object.keys(newData).forEach((k) => {
            if (!k.includes('.')) { next[k] = newData[k]; return; }
            const path = k.split('.');
            let at = next;
            path.slice(0, -1).forEach((part) => {
              at[part] = { ...((at[part] && typeof at[part] === 'object') ? at[part] : {}) };
              at = at[part];
            });
            at[path[path.length - 1]] = newData[k];
          });
          return next;
        });
        if (newData.currency) setCurrentCurrency(newData.currency);
      }
    } catch (error) {
      console.error('Failed to save data:', error);
      toast.error('Failed to save data');
    }
  };

  // purple_01: Neutral and the Pastel coin icon were removed. Anyone still on
  // them moves back to Pink and the normal icon, once.
  useEffect(() => {
    if (!userData) return;
    const fix = {};
    if (userData.theme && !['pink', 'blue', 'warm'].includes(userData.theme)) fix.theme = 'pink';
    if (userData.app_icon === 'coins') {
      fix.app_icon = 'default';
      AppIcon.setIcon({ name: 'default' }).catch(() => {});
    }
    try { window.localStorage.removeItem('worthly_pastel'); } catch (e) { /* private mode */ }
    if (Object.keys(fix).length) {
      // Fix it here straight away too, so a failed save (offline) can't leave
      // the tabs styled for a theme that no longer exists.
      setUserData(prev => (prev ? { ...prev, ...fix } : prev));
      saveUserData(fix);
    }
  }, [userData?.theme, userData?.app_icon]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async () => {
    console.log('LOGIN TAPPED - isNative:', Capacitor.isNativePlatform(), 'platform:', Capacitor.getPlatform());
    const clientId = '138015137151-cvr8ckmhr4fbivc40visd8v7lq93ttq4.apps.googleusercontent.com';
    const scope = encodeURIComponent('openid email profile');
    const redirectUri = encodeURIComponent('https://worthly-app.netlify.app/auth/callback');
    const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=consent`;
    if (Capacitor.isNativePlatform()) {
      // Use backend callback flow for iOS
      const iosRedirectUri = encodeURIComponent('https://worthly-backend-production.up.railway.app/api/auth/google/callback');
      const iosUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${iosRedirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=consent&state=${iosRedirectUri}`;
      console.log('Opening browser with URL:', iosUrl.substring(0, 100));
      try {
        await Browser.open({ url: iosUrl });
        console.log('Browser.open succeeded');
      } catch (err) {
        console.error('Browser.open FAILED:', err, JSON.stringify(err));
      }
    } else {
      window.location.href = url;
    }
  };

  const handleAppleSignIn = async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://worthly-backend-production.up.railway.app';

    if (Capacitor.isNativePlatform()) {
      try {
        const result = await AppleSignIn.startSignIn();

        const response = await fetch(`${BACKEND_URL}/api/auth/apple`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            identity_token: result.identityToken,
            authorization_code: result.authorizationCode,
            user: result.user,
            email: result.email,
            full_name: {
              givenName: result.givenName,
              familyName: result.familyName,
            },
            source: 'ios',
          }),
        });
        const data = await response.json();
        if (data.session_token) {
          await keychainService.setItem('session_token', data.session_token);
          window.location.href = '/';
        } else {
          alert('Sign in failed. Please try again.');
        }
      } catch (err) {
        console.error('Apple Sign In error:', err);
        if (err.message !== 'The operation was cancelled by the user.') {
          alert('Apple Sign In failed. Please try again.');
        }
      }
    } else {
      const clientId = 'com.layemi.worthly.web';
      const redirectURI = encodeURIComponent(`${BACKEND_URL}/api/auth/apple/callback`);
      const scope = encodeURIComponent('name email');
      const state = 'worthly_apple_web';

      const appleAuthURL = 'https://appleid.apple.com/auth/authorize?' +
        `client_id=${clientId}` +
        `&redirect_uri=${redirectURI}` +
        '&response_type=code id_token' +
        `&scope=${scope}` +
        '&response_mode=form_post' +
        `&state=${state}`;

      window.location.href = appleAuthURL;
    }
  };

  const logout = async () => {
    const token = await keychainService.getItem('session_token');
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    await keychainService.removeItem('session_token');
    setUser(null);
    setUserData(null);
    navigate('/');
  };

  // Check for existing session
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // --- Backend health check (outage detection) ---
        // Fail-SAFE: only block the app on a CLEAR down signal
        // (Railway edge fallback). On timeout/ambiguity we let the
        // app continue so we never lock out users over a flaky check.
        try {
          const hc = new AbortController();
          const hcTimer = setTimeout(() => hc.abort(), 6000);
          const hcRes = await fetch(`${API_URL}/api/health`, { signal: hc.signal });
          clearTimeout(hcTimer);
          if (isRailwayFallback(hcRes)) {
            setBackendDown(true);
            setLoading(false);
            return;
          }
        } catch (hcErr) {
          // Network/timeout: ambiguous. Do NOT block — fail safe.
          console.warn('[Health] check inconclusive, continuing', hcErr);
        }

        // Warm the in-memory token cache
        await keychainService.init();

        // One-time migration: localStorage → Keychain
        const legacyToken = localStorage.getItem('session_token');
        if (legacyToken) {
          try {
            await keychainService.setItem('session_token', legacyToken);
            const verify = await keychainService.getItem('session_token');
            if (verify) {
              localStorage.removeItem('session_token');
              console.log('[Auth] Migrated session_token to Keychain');
            }
          } catch (e) {
            console.warn('[Auth] Keychain migration failed, keeping localStorage', e);
          }
        }
        const token = await keychainService.getItem('session_token');
        if (!token) { setLoading(false); return; }
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const authData = await response.json();
          setUser(authData);
          // Load user data and settings in parallel, wait for both to complete
          const [userDataResult] = await Promise.all([loadUserData(), loadAppSettings()]);
          // Only set loading to false after userData is loaded
          if (userDataResult) {
            setLoading(false);
          } else {
            // If user data failed to load, still set loading false
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.log('Not authenticated');
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Update PWA manifest and icons (only when user changes, not userData)
  useEffect(() => {
    if (user) {
      // Set the new Worthly app icon for all users
      const logoPath = '/worthly-app-icon.png';
      
      // Remove existing apple-touch-icon to set static one
      const existingAppleIcons = document.querySelectorAll('link[rel="apple-touch-icon"], link[rel="apple-touch-icon-precomposed"]');
      existingAppleIcons.forEach(icon => icon.remove());
      
      // Create apple-touch-icon
      const appleIcon = document.createElement('link');
      appleIcon.rel = 'apple-touch-icon';
      appleIcon.sizes = '180x180';
      appleIcon.href = logoPath;
      document.head.appendChild(appleIcon);
      
      // Also add apple-touch-icon-precomposed for older iOS
      const appleIconPrecomposed = document.createElement('link');
      appleIconPrecomposed.rel = 'apple-touch-icon-precomposed';
      appleIconPrecomposed.sizes = '180x180';
      appleIconPrecomposed.href = logoPath;
      document.head.appendChild(appleIconPrecomposed);
      
      // Update favicon
      let favicon = document.querySelector('link[rel="icon"]');
      if (favicon) favicon.remove();
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.href = logoPath;
      document.head.appendChild(favicon);
    }
  }, [user]);

  // Keep page title as Worthly
  useEffect(() => {
    document.title = 'Worthly';
  }, []);

  // Add app-loaded class when loading is complete
  useEffect(() => {
    if (!loading) {
      document.documentElement.classList.add('app-loaded');
    }
    return () => {
      document.documentElement.classList.remove('app-loaded');
    };
  }, [loading]);

  if (loading) {
    return (
      <LoadingScreen 
        title="Setting up your finances..." 
        subtitle="Organizing your budget" 
      />
    );
  }

  // Whole-app gate: if the backend is unreachable (or dev toggle on),
  // show the calm outage screen instead of a broken app.
  if (backendDown || devForceDown) {
    return (
      <ServiceDownScreen
        preview={devForceDown && !backendDown}
        onRetry={() => { setBackendDown(false); setDevForceDown(false); window.location.reload(); }}
      />
    );
  }

  // Admin-only dev toggle: forces the outage screen for testing.
  // Listen for a custom event so it can be triggered from Settings.
  // (wired separately; safe no-op if never fired)
  if (typeof window !== 'undefined' && !window.__worthlyDevDownWired) {
    window.__worthlyDevDownWired = true;
    window.addEventListener('worthly:dev-force-down', () => setDevForceDown(true));
  }

  return children({ user, userData, appSettings, login, handleAppleSignIn, logout, saveUserData, loadUserData, loadAppSettings, setAppSettings });
};

// ============== Auth Callback ==============
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for session_token in hash (iOS OAuth callback)
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const token = hash.split('session_id=')[1];
        if (token) {
          await keychainService.setItem('session_token', token);
          window.location.href = '/dashboard';
          return;
        }
      }

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');

      if (!code) {
        navigate('/');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: code, redirect_uri: window.location.origin + '/auth/callback' })
        });

        if (response.ok) {
          const data = await response.json();
          await keychainService.setItem('session_token', data.session_token);
          window.location.href = '/dashboard';
          return;
        } else {
          toast.error('Authentication failed. Please try again.');
          navigate('/');
        }
      } catch (error) {
        console.error('Auth error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/');
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <LoadingScreen 
      title="Signing you in..." 
      subtitle="Please wait" 
    />
  );
};

// ============== Login Page ==============
// ============== Login Page (Two-Screen Welcome Flow) ==============
// ============== Login Page (Two-Screen Welcome Flow) ==============
// ============== Login Page (Two-Screen Welcome Flow) ==============
// ============== Login Page (Two-Screen Welcome Flow) ==============
// ============== Login Page (Two-Screen Welcome Flow) ==============
// ============== Login Page (Two-Screen Welcome Flow) ==============
// w2_05: the welcome, sign up and name screens live in
// components/onboarding/Worthly2Welcome.jsx now.
// w2_09: which look the widgets draw in. Worthly 2.0 sends "plan".
const widgetThemeFor = (userData, purple) => (
  userData?.worthly2 === true && (userData?.theme || 'pink') === 'pink' ? 'plan' : purple ? 'purple' : (userData?.theme || 'pink')
);

const LoginPage = W2LoginPage;
const NameEntryPage = W2NameEntryPage;

// ============== Tab Navigation ==============
// Note: TabNav is now imported from ./components/common/TabNav

// ============== Sticky Header ==============

// ============== Profile Picture ==============
// Note: createCroppedImage is now imported from ./utils/imageHelpers


// ============== Profile Picture ==============
// ProfilePicture has been extracted to: /components/common/ProfilePicture.jsx


// ============== Settings Dialog ==============

// ============== Settings Dialog ==============
// SettingsDialog has been extracted to: /components/settings/SettingsDialog.jsx


// ============== Day Cell ==============
// Note: DayCell is now imported from ./components/calendar/DayCell

// ============== Stats Card ==============
// Note: StatsCard is now imported from ./components/common/StatsCard

// ============== Net Worth Chart ==============
const NetWorthChart = ({ data, theme }) => {
  const themeColors = THEME_COLORS[theme] || THEME_COLORS.pink;
  
  if (!data || data.length < 2) {
    return (
      <div className="text-center py-8 text-gray-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Add at least 2 months of data to see your trend chart</p>
      </div>
    );
  }

  const chartData = [...data]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map(entry => ({
      month: entry.month.slice(5) + '/' + entry.month.slice(2, 4),
      netWorth: entry.net_worth,
      savings: entry.total_savings,
      debts: entry.total_debts
    }));

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={themeColors.primary} stopOpacity={0.8}/>
              <stop offset="95%" stopColor={themeColors.primary} stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={themeColors.light} />
          <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
          <Tooltip 
            contentStyle={{ backgroundColor: 'white', border: `1px solid ${themeColors.light}`, borderRadius: '12px' }}
            formatter={(value) => [formatCurrency(value), 'Net Worth']}
          />
          <Area type="monotone" dataKey="netWorth" stroke={themeColors.primary} strokeWidth={3} fill="url(#netWorthGradient)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Chart that generates data from monthly tracked items (sinking funds, investments, assets, debts)
const NetWorthChartFromMonthly = ({ sinkingFunds, investments, assets, debts, extraPayments, theme }) => {
  const [showProjected, setShowProjected] = useState(false);
  const themeColors = THEME_COLORS[theme] || THEME_COLORS.pink;
  
  // Theme-aware colors for projected: warm uses forest green (#1B4027), others use vibrant blue (#2b6cee)
  const isWarm = theme === 'warm';
  const projectedColor = isWarm ? '#1B4027' : '#2b6cee';
  const projectedGridColor = isWarm ? '#D8E8DC' : '#dbeafe';
  
  // Collect all unique months from all data sources
  const allMonths = new Set();
  
  const collectMonths = (items) => {
    (items || []).forEach(item => {
      if (item.current_by_month) {
        Object.keys(item.current_by_month).forEach(month => allMonths.add(month));
      }
    });
  };
  
  collectMonths(sinkingFunds);
  collectMonths(investments);
  collectMonths(assets);
  collectMonths(debts);
  
  if (allMonths.size < 2) {
    return (
      <div className="text-center py-8 text-gray-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Add data for at least 2 months to see your trend chart</p>
      </div>
    );
  }
  
  // Sort months and generate chart data
  const sortedMonths = Array.from(allMonths).sort();
  
  const getMonthTotal = (items, month) => {
    return (items || []).reduce((sum, item) => {
      return sum + (item.current_by_month?.[month] || 0);
    }, 0);
  };
  
  // Calculate total pending extra payments for projections
  const totalPendingPayments = (extraPayments || [])
    .filter(p => !p.moved_to_account && countsInProjection(p))
    .reduce((sum, p) => sum + signOf(p) * (p.amount || 0), 0);
  
  const chartData = sortedMonths.map(month => {
    const savings = getMonthTotal(sinkingFunds, month);
    const investmentsTotal = getMonthTotal(investments, month);
    const assetsTotal = getMonthTotal(assets, month);
    const debtsTotal = getMonthTotal(debts, month);
    const totalAssets = savings + investmentsTotal + assetsTotal;
    const netWorth = totalAssets - debtsTotal;
    
    // Projected = actual net worth + all pending extra payments
    const projectedNetWorth = netWorth + totalPendingPayments;
    
    return {
      month: month.slice(5) + '/' + month.slice(2, 4),
      netWorth,
      projectedNetWorth,
      totalAssets,
      debts: debtsTotal
    };
  });
  
  const dataKey = showProjected ? 'projectedNetWorth' : 'netWorth';
  const gradientId = showProjected ? 'projectedNetWorthGradient' : 'netWorthGradient2';
  const strokeColor = showProjected ? projectedColor : themeColors.primary;
  const gridColor = showProjected ? projectedGridColor : themeColors.light;
  const label = showProjected ? 'Projected Net Worth' : 'Net Worth';
  
  return (
    <div>
      {/* Toggle buttons */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setShowProjected(false)}
          className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
            !showProjected 
              ? 'theme-bg-500 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Actual
        </button>
        <button
          onClick={() => setShowProjected(true)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{
            backgroundColor: showProjected ? projectedColor : '#f3f4f6',
            color: showProjected ? 'white' : '#4b5563'
          }}
        >
          Projected
        </button>
      </div>
      
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 25, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="netWorthGradient2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={themeColors.primary} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={themeColors.primary} stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="projectedNetWorthGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={projectedColor} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={projectedColor} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip 
              contentStyle={{ backgroundColor: 'white', border: `1px solid ${gridColor}`, borderRadius: '12px' }}
              formatter={(value) => [formatCurrency(value), label]}
            />
            <Area 
              type="monotone" 
              dataKey={dataKey} 
              stroke={strokeColor} 
              strokeWidth={3} 
              fill={`url(#${gradientId})`}
              label={<CustomDataLabel fill={strokeColor} />}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      <p className="text-xs text-gray-400 text-center mt-2">
        {showProjected 
          ? 'End of month net worth + all pending extra payments for the year'
          : 'End of month net worth (current month shows latest value)'}
      </p>
    </div>
  );
};

// Custom label component for chart data points
// Note: CustomDataLabel is now imported from ./components/charts/CustomDataLabel

// Savings Trend Chart - shows total savings over time from sinking funds
// Now includes toggle between actual and projected savings
const SavingsTrendChart = ({ sinkingFunds, extraPayments, theme = 'pink' }) => {
  const [showProjected, setShowProjected] = useState(false);
  
  // Theme-aware colors: warm theme uses forest green for both, others use emerald/vibrant blue
  const isWarm = theme === 'warm';
  const actualColor = isWarm ? '#2D5A3D' : '#34d399';
  const projectedColor = isWarm ? '#1B4027' : '#2b6cee';
  const actualGridColor = isWarm ? '#D8E8DC' : '#d1fae5';
  const projectedGridColor = isWarm ? '#D8E8DC' : '#dbeafe';
  
  // Collect all unique months from sinking funds
  const allMonths = new Set();
  
  (sinkingFunds || []).forEach(fund => {
    if (fund.current_by_month) {
      Object.keys(fund.current_by_month).forEach(month => allMonths.add(month));
    }
  });
  
  if (allMonths.size < 2) {
    return (
      <div className="text-center py-8 text-gray-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Add data for at least 2 months to see your savings trend</p>
      </div>
    );
  }
  
  // Sort months and generate chart data
  const sortedMonths = Array.from(allMonths).sort();
  
  // Calculate total of all pending extra payments for the year
  const totalPendingPayments = (extraPayments || [])
    .filter(p => !p.moved_to_account && countsInProjection(p))
    .reduce((sum, p) => sum + signOf(p) * (p.amount || 0), 0);
  
  const chartData = sortedMonths.map((month, index) => {
    const totalSavings = (sinkingFunds || []).reduce((sum, fund) => {
      return sum + (fund.current_by_month?.[month] || 0);
    }, 0);
    
    // Projected = actual savings for that month + all pending payments for the year
    const projectedSavings = totalSavings + totalPendingPayments;
    
    return {
      month: month.slice(5) + '/' + month.slice(2, 4),
      savings: totalSavings,
      projected: projectedSavings
    };
  });
  
  const dataKey = showProjected ? 'projected' : 'savings';
  const gradientId = showProjected ? 'projectedGradient' : 'savingsGradient';
  const strokeColor = showProjected ? projectedColor : actualColor;
  const gridColor = showProjected ? projectedGridColor : actualGridColor;
  const label = showProjected ? 'Projected Savings' : 'Actual Savings';
  
  return (
    <div>
      {/* Toggle buttons */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setShowProjected(false)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{ 
            backgroundColor: !showProjected ? actualColor : '#f3f4f6',
            color: !showProjected ? 'white' : '#4b5563'
          }}
        >
          Actual
        </button>
        <button
          onClick={() => setShowProjected(true)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{ 
            backgroundColor: showProjected ? projectedColor : '#f3f4f6',
            color: showProjected ? 'white' : '#4b5563'
          }}
        >
          Projected
        </button>
      </div>
      
      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 25, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="savingsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={actualColor} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={actualColor} stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="projectedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={projectedColor} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={projectedColor} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip 
              contentStyle={{ backgroundColor: 'white', border: `1px solid ${gridColor}`, borderRadius: '12px' }}
              formatter={(value) => [formatCurrency(value), label]}
            />
            <Area 
              type="monotone" 
              dataKey={dataKey} 
              stroke={strokeColor} 
              strokeWidth={3} 
              fill={`url(#${gradientId})`}
              label={<CustomDataLabel fill={strokeColor} />}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      <p className="text-xs text-gray-400 text-center mt-2">
        {showProjected 
          ? 'End of month balance + all pending extra payments for the year'
          : 'End of month balance (current month shows latest value)'}
      </p>
    </div>
  );
};

// Investments Trend Chart - shows total investments over time
// Includes toggle between actual and projected investments
const InvestmentsTrendChart = ({ investmentAccounts, extraPayments, transactions = [], theme = 'pink' }) => {
  const [showProjected, setShowProjected] = useState(false);
  
  // Theme-aware colors: warm theme uses forest green for both, others use emerald/vibrant blue
  const isWarm = theme === 'warm';
  const actualColor = isWarm ? '#2D5A3D' : '#34d399';
  const projectedColor = isWarm ? '#1B4027' : '#2b6cee';
  const actualGridColor = isWarm ? '#D8E8DC' : '#d1fae5';
  const projectedGridColor = isWarm ? '#D8E8DC' : '#dbeafe';
  
  // Get investment balance for a specific month
  const getInvestmentBalanceForMonth = (invId, monthKey) => {
    const inv = (investmentAccounts || []).find(i => i.id === invId);
    if (!inv) return 0;
    return investmentBalance(inv, transactions, monthKey, { cutoffDay: monthEndDay(monthKey) }); // dark_13
  };
  
  // Collect all unique months from investment overrides and transactions
  const allMonths = new Set();
  
  (investmentAccounts || []).forEach(inv => {
    if (inv.override_by_month) {
      Object.keys(inv.override_by_month).forEach(month => allMonths.add(month));
    }
  });
  
  // Also add months from transactions
  (transactions || []).forEach(t => {
    if ((t.account_type === 'investment' || t.interest_account_type === 'investment') && t.date) {
      const month = t.date.slice(0, 7);
      allMonths.add(month);
    }
  });
  
  // Add current month if we have investment accounts
  if ((investmentAccounts || []).length > 0) {
    const currentMonth = getMonthKey(new Date());
    allMonths.add(currentMonth);
  }
  
  if (allMonths.size < 2) {
    return (
      <div className="text-center py-8 text-gray-400">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Add data for at least 2 months to see your investments trend</p>
      </div>
    );
  }
  
  // Sort months and generate chart data
  const sortedMonths = Array.from(allMonths).sort();
  
  // Calculate total of all pending investment allocations
  const totalPendingInvestments = (extraPayments || [])
    .filter(p => !p.moved_to_account && p.investment_allocations && countsInProjection(p))
    .reduce((sum, p) => {
      const invTotal = Object.values(p.investment_allocations || {}).reduce((s, amt) => s + amt, 0);
      return sum + invTotal;
    }, 0);
  
  const chartData = sortedMonths.map((month) => {
    const totalInvestments = (investmentAccounts || []).reduce((sum, inv) => {
      return sum + getInvestmentBalanceForMonth(inv.id, month);
    }, 0);
    
    // Projected = actual investments for that month + all pending investment allocations
    const projectedInvestments = totalInvestments + totalPendingInvestments;
    
    return {
      month: month.slice(5) + '/' + month.slice(2, 4),
      investments: totalInvestments,
      projected: projectedInvestments
    };
  });
  
  const dataKey = showProjected ? 'projected' : 'investments';
  const gradientId = showProjected ? 'investmentProjectedGradient' : 'investmentActualGradient';
  const strokeColor = showProjected ? projectedColor : actualColor;
  const gridColor = showProjected ? projectedGridColor : actualGridColor;
  const label = showProjected ? 'Projected Investments' : 'Actual Investments';
  
  return (
    <div>
      {/* Toggle buttons */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setShowProjected(false)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{ 
            backgroundColor: !showProjected ? actualColor : '#f3f4f6',
            color: !showProjected ? 'white' : '#4b5563'
          }}
        >
          Actual
        </button>
        <button
          onClick={() => setShowProjected(true)}
          className="px-3 py-1.5 text-xs font-medium rounded-full transition-all"
          style={{ 
            backgroundColor: showProjected ? projectedColor : '#f3f4f6',
            color: showProjected ? 'white' : '#4b5563'
          }}
        >
          Projected
        </button>
      </div>
      
      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 25, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="investmentActualGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={actualColor} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={actualColor} stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="investmentProjectedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={projectedColor} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={projectedColor} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
            <Tooltip 
              contentStyle={{ backgroundColor: 'white', border: `1px solid ${gridColor}`, borderRadius: '12px' }}
              formatter={(value) => [formatCurrency(value), label]}
            />
            <Area 
              type="monotone" 
              dataKey={dataKey} 
              stroke={strokeColor} 
              strokeWidth={3} 
              fill={`url(#${gradientId})`}
              label={<CustomDataLabel fill={strokeColor} />}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      <p className="text-xs text-gray-400 text-center mt-2">
        {showProjected 
          ? 'Current balance + all pending future income allocations'
          : 'Current balance (manual overrides where set)'}
      </p>
    </div>
  );
};

// ============== Subscription Aware Routes ==============
const SubscriptionAwareRoutes = ({ user, userData, appSettings, logout, saveUserData, loadAppSettings, setAppSettings, loadUserData }) => {
  const { subscription, showTrialPrompt, setShowTrialPrompt, loading } = useSubscription();
  const [showTrialModal, setShowTrialModal] = useState(false);
  // Bumped whenever the tour transitions, so the trial-prompt effect re-evaluates.
  const [tourTick, setTourTick] = useState(0);
  useEffect(() => {
    const handler = () => setTourTick(t => t + 1);
    window.addEventListener('worthly:tour-state-changed', handler);
    return () => window.removeEventListener('worthly:tour-state-changed', handler);
  }, []);
  
  // Check if we should show trial prompt (new user who hasn't made a choice yet)
  //
  // Tour/paywall ordering:
  //   tour pending/in_progress -> wait (tour runs first)
  //   tour completed           -> show paywall (after celebration)
  //   tour skipped_once        -> show paywall (they bailed on the tour, give them the offer)
  //   tour skipped_twice       -> don't show (already saw it on first skip)
  // Re-runs when `tourTick` changes, so the TourController can signal us.
  useEffect(() => {
    if (!loading && subscription) {
      // Show trial prompt if user has no subscription status set (new user after onboarding)
      const noSubscriptionChoice = !subscription.status || (subscription.status === 'free' && !subscription.trial_start && subscription.trial_start !== null);
      // Only show if they haven't explicitly skipped (trial_start would be null if skipped, undefined if never prompted)
      if (noSubscriptionChoice && userData?.onboarding_complete && userData?.tutorial_completed !== false) {
        // Tour gate: only proceed if the tour has completed or was skipped once.
        const tourStatus = getTourState(user?.user_id)?.status;
        const tourDone = tourStatus === 'completed' || tourStatus === 'skipped_once';
        // skipped_twice means they've already seen the paywall on first skip -> don't show again.
        if (!tourDone) return;

        // Check if this is first time seeing this prompt
        const hasSeenPrompt = localStorage.getItem(`trial_prompt_seen_${user?.user_id}`);
        if (!hasSeenPrompt) {
          setShowTrialModal(true);
        }
      }
    }
  }, [loading, subscription, userData, user, tourTick]);

  const handleCloseTrialPrompt = () => {
    setShowTrialModal(false);
    if (user?.user_id) {
      localStorage.setItem(`trial_prompt_seen_${user.user_id}`, 'true');
    }
    try { window.dispatchEvent(new CustomEvent('worthly:trial-prompt-decided')); } catch (e) {}
  };

  const handlePurchaseSuccess = () => {
    customToast.celebration('Welcome to Worthly Premium!');
    try { window.dispatchEvent(new CustomEvent('worthly:trial-prompt-decided')); } catch (e) {}
  };

  return (
    <>
      {/* Trial Prompt Modal */}
      {showTrialModal && <TrialPrompt onClose={handleCloseTrialPrompt} onPurchaseSuccess={handlePurchaseSuccess} />}
      
      {/* Paywall Modal (shown when user tries to access premium feature) */}
      <PaywallModal onPurchaseSuccess={handlePurchaseSuccess} />
      
      {/* Main Routes */}
      <Routes>
        <Route path="/" element={<Dashboard user={user} userData={userData} appSettings={appSettings} logout={logout} saveUserData={saveUserData} loadAppSettings={loadAppSettings} setAppSettings={setAppSettings} loadUserData={loadUserData} />} />
        <Route path="/dashboard" element={<Dashboard user={user} userData={userData} appSettings={appSettings} logout={logout} saveUserData={saveUserData} loadAppSettings={loadAppSettings} setAppSettings={setAppSettings} loadUserData={loadUserData} />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
    </>
  );
};

// w2_21: a full page on top of Home that swipes back to it, like Notes and Reminders.
const SwipeLayer = ({ on, onBack, children }) => {
  const ref = useRef(null);
  useSwipeBack(() => ref.current, () => { if (onBack) onBack(); }, !!on);
  if (!on) return children;
  return (
    <div ref={ref} className="wy-swipe-layer" data-swipe-layer="1" style={{ position: 'fixed', inset: 0, zIndex: 55, background: '#F4F1FB', overflowY: 'auto', overscrollBehavior: 'contain', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 110px)' }}>
      {children}
    </div>
  );
};

// ============== Dashboard (Main App) ==============
// plan_20: in Money plan, Notes, Reminders and Budgets sit on top of Home,
// so swiping back shows Home underneath.
const NotesLayer = ({ on, children }) => (on ? (
  <div className="wy-notes-layer" data-swipe-layer="1" style={{ position: 'fixed', inset: 0, zIndex: 55, background: '#F4F1FB', overflowY: 'auto', overscrollBehavior: 'contain', paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 110px)' }}>
    {children}
  </div>
) : children);

const Dashboard = ({ user, userData, appSettings, logout, saveUserData, loadAppSettings, setAppSettings, loadUserData }) => {
  // siri_02: reload your data whenever Worthly comes back to the front, so an
  // expense added with Siri is there before anything in the app saves.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !loadUserData) return undefined;
    const sub = CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) loadUserData();
    });
    return () => { Promise.resolve(sub).then((h) => { if (h && h.remove) h.remove(); }); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [moneyPlanOpen, setMoneyPlanOpen] = useState(false); // mp_01
  const [moneyPlanStart, setMoneyPlanStart] = useState(null); // plan_27: 'checkup' opens straight on the check-up
  // Purple (admin-only): the widgets follow it too, as their own 'purple' theme.
  const purpleWidgets = usePurple();
  // The funds widget mirrors the Funds tab, free-tier cap included
  const { getLimit: getWidgetLimit } = useSubscription();
  const [activeTab, setActiveTab] = useState(() => {
    const widgetTab = localStorage.getItem('widget_open_tab');
    if (widgetTab) {
      localStorage.removeItem('widget_open_tab');
      return widgetTab;
    }
    return 'rules';
  });
  const [widgetOpenAddMenu, setWidgetOpenAddMenu] = useState(() => {
    const action = localStorage.getItem('widget_budget_action');
    if (action === 'addTransaction') {
      localStorage.removeItem('widget_budget_action');
      return true;
    }
    return false;
  });
  // Money Club opened from a notification or Settings: go to Home, which opens the page.
  useEffect(() => {
    const goHome = () => setActiveTab('rules');
    window.addEventListener(OPEN_MONEY_CLUB_EVENT, goHome);
    return () => window.removeEventListener(OPEN_MONEY_CLUB_EVENT, goHome);
  }, []);
  // Tour: imperative trigger for BudgetTab's Add menu (step 2 of the tour).
  const [tourOpenBudgetAdd, setTourOpenBudgetAdd] = useState(false);
  // Tour: BudgetTab tells us when the Add sheet opens/closes so we can
  // suppress the coach-mark while the user is filling the form.
  const [isBudgetSheetOpen, setIsBudgetSheetOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      setIsBudgetSheetOpen(!!e?.detail?.open);
    };
    window.addEventListener('worthly:budget-sheet-state', handler);
    return () => window.removeEventListener('worthly:budget-sheet-state', handler);
  }, []);
  // Tour: CalendarTab tells us when a day-type/note dialog is open so we
  // can suppress the coach-mark on step 3.
  const [isCalendarDialogOpen, setIsCalendarDialogOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => { setIsCalendarDialogOpen(!!e?.detail?.open); };
    window.addEventListener('worthly:calendar-dialog-state', handler);
    return () => window.removeEventListener('worthly:calendar-dialog-state', handler);
  }, []);
  // Tour: when the tour ends (complete/skip/start-trial), force-navigate
  // to the Home tab. Defensive — TourController already does this, but this
  // covers any edge case where its setActiveTab call was superseded.
  useEffect(() => {
    const handler = () => {
      if (typeof setActiveTab === 'function') {
        // Pass 9: actual tab id is 'rules' (legacy name for the home screen)
        setActiveTab('rules');
      }
    };
    window.addEventListener('worthly:tour-ended', handler);
    return () => window.removeEventListener('worthly:tour-ended', handler);
    // setActiveTab is stable (from useState), no dependency needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tour: when the trial prompt closes (after the tour ends and the auto-
  // prompt appears), also force-navigate Home. This handles the case where
  // the user was on Wishlist at tour end, the paywall opened, and by the
  // time the paywall closed the activeTab state hadn't settled. Belt-and-
  // suspenders with the tour-end listener above.
  useEffect(() => {
    const handler = () => {
      if (typeof setActiveTab === 'function') {
        // Pass 9: actual tab id is 'rules' (legacy name for the home screen)
        setActiveTab('rules');
      }
    };
    window.addEventListener('worthly:trial-prompt-decided', handler);
    return () => window.removeEventListener('worthly:trial-prompt-decided', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStartSlide, setTutorialStartSlide] = useState(0);
  const [showPremiumFeatures, setShowPremiumFeatures] = useState(false);
  const [showReviewFallback, setShowReviewFallback] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const mainRef = useRef(null);

  // One-time seed of the Contributions Planner setting (Canada-only feature).
  // An existing user with Canadian credit score tracking on is almost certainly
  // Canadian, so the planner starts on for them. Written once and stamped with
  // contributionsSeeded; after that the two settings never influence each other
  // again. Spreads the existing plans object so sources, room and notes survive.
  const contributionsSeedRef = useRef(null);
  useEffect(() => {
    if (!userData) return;
    if (userData.plans?.contributionsSeeded === true) return;
    const uid = user?.user_id || 'unknown';
    if (contributionsSeedRef.current === uid) return;
    contributionsSeedRef.current = uid;
    saveUserData({
      plans: {
        ...(userData.plans || {}),
        contributionsEnabled: (userData.credit_score?.enabled !== false),
        contributionsSeeded: true,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.plans?.contributionsSeeded, user?.user_id]);

  // Review prompt trigger: increment open count and check trigger on dashboard mount
  useEffect(() => {
    reviewService.incrementOpenCountOncePerSession();
    const trigger = reviewService.checkTrigger();
    if (trigger === 'native') {
      // small delay so the dashboard has a chance to render first
      const t = setTimeout(() => {
        if (!canShowPopup('review')) return; // another pop-up showed too recently
        markPopupSpacing('review');
        reviewService.triggerNativePrompt();
      }, 2000);
      return () => clearTimeout(t);
    } else if (trigger === 'fallback') {
      const t = setTimeout(() => {
        if (!canShowPopup('review')) return; // another pop-up showed too recently
        markPopupSpacing('review');
        reviewService.markPopupShown();
        setShowReviewFallback(true);
      }, 2000);
      return () => clearTimeout(t);
    }
  }, []);

  // Admin preview: lets the review pop-up be opened on demand for testing
  useEffect(() => {
    const h = () => setShowReviewFallback(true);
    window.addEventListener('worthly:dev-preview-review', h);
    return () => window.removeEventListener('worthly:dev-preview-review', h);
  }, []);

  // Update prompt: once per app open, ask the backend whether a newer version
  // is live on the App Store (rules in utils/updatePrompt.js). Only shown on
  // Home, never during the tour, and spaced from the other pop-ups.
  const [updateCandidate, setUpdateCandidate] = useState(null);
  const updateShownRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    checkForUpdate(API_URL).then((v) => { if (!cancelled && v) setUpdateCandidate(v); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!updateCandidate || updateShownRef.current) return;
    if (activeTab !== 'rules') return; // Home only
    const t = setTimeout(() => {
      if (updateShownRef.current) return;
      if (window.__worthlyTourActive) return;
      const tourStatus = getTourState(user?.user_id)?.status;
      if (tourStatus === 'pending' || tourStatus === 'in_progress') return;
      if (!canShowPopup('update')) return; // another pop-up showed too recently
      updateShownRef.current = true;
      markPopupSpacing('update');
      markUpdatePromptShown(updateCandidate);
      setShowUpdatePrompt(true);
    }, 3500);
    return () => clearTimeout(t);
  }, [updateCandidate, activeTab, user?.user_id]);

  // Admin preview: opens the update pop-up on demand (doesn't count as shown)
  useEffect(() => {
    const h = () => setShowUpdatePrompt(true);
    window.addEventListener('worthly:dev-preview-update', h);
    return () => window.removeEventListener('worthly:dev-preview-update', h);
  }, []);
  
  // Reset scroll position on initial dashboard mount
  useEffect(() => {
    // Force scroll to absolute top
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);
  
  // Function to show tutorial for a specific tab
  // w2_05: each help button opens its own tab's walkthrough page
  const showTutorialForTab = (tabName) => {
    const slideMap = TUTORIAL_SLIDES;
    setTutorialStartSlide(slideMap[tabName] || 0);
    setShowTutorial(true);
  };

  // Tab titles for sticky header
  const tabTitles = {
    rules: 'Home',
    budget: 'Monthly Budget',
    calendar: 'No Spend Calendar',
    savings: 'Savings & Investments',
    wishlist: 'Wishlist',
    networth: 'Net Worth'
  };

  // Scroll detection for sticky header
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Show sticky header after scrolling 50px down
    };

    // Ensure header is hidden on initial load (important for PWA)
    window.scrollTo(0, 0);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Dismiss keyboard on Enter/Return key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && document.activeElement && document.activeElement.tagName === 'INPUT') {
        document.activeElement.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reset scroll when tab changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  // Reminders due today or overdue, on the app icon. Lives here rather than in
  // NotesTab so the badge is right even for someone who never opens that tab,
  // and runs on load so it is restored after AppDelegate clears it at launch.
  useEffect(() => {
    // Reminders switched off in Settings means no badge either, or the
    // user is left with a number they have no way to clear.
    const remindersOn = userData?.notification_preferences?.reminders !== false;
    setBadge(remindersOn ? countDueNow(userData?.plans?.reminders) : 0);
  }, [userData?.plans?.reminders, userData?.notification_preferences?.reminders]);

  // iOS holds its own copy of scheduled alerts, so switching Reminders off
  // has to tell it to drop them - saving the preference alone does nothing
  // and queued alerts keep firing. Rebuilding from the stored reminders
  // restores them all when it goes back on.
  useEffect(() => {
    const remindersOn = userData?.notification_preferences?.reminders !== false;
    syncReminderNotifications(remindersOn ? (userData?.plans?.reminders || []) : [])
      .catch(() => {});
  }, [userData?.plans?.reminders, userData?.notification_preferences?.reminders]);

  // One-time migration: backfill month_key from date for old transactions
  useEffect(() => {
    if (!userData?.transactions) return;
    const txns = userData.transactions;
    const needsFix = txns.some(t => !t.month_key && t.date);
    if (needsFix) {
      const fixed = txns.map(t => {
        if (!t.month_key && t.date) {
          const d = new Date(t.date);
          return { ...t, month_key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` };
        }
        return t;
      });
      saveUserData({ transactions: fixed });
    }
  }, [userData?.transactions?.length]);

  // Month-end review: remember the day the app was last opened (the phone's own
  // date + time zone) so the 6pm reminder on the 1st only goes to people who
  // haven't opened the app yet that day.
  useEffect(() => {
    if (!userData) return;
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const mer = userData.month_end_review || {};
    if (mer.last_open === today && mer.tz_offset === d.getTimezoneOffset()) return;
    saveUserData({ month_end_review: { ...mer, last_open: today, tz_offset: d.getTimezoneOffset() } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.month_end_review?.last_open, !!userData]);

  // Month-end review alert: 8am on the 1st, queued on the phone (no backend).
  // Separate from reminders - own kind tag and id, so neither clears the other.
  // Re-checked on open, when the review is opened/skipped, when transactions
  // change, and when reminders change (that's usually when permission is given).
  useEffect(() => {
    if (!userData) return;
    syncMonthEndNotification(userData).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!userData, userData?.month_end_review?.seen, userData?.month_end_review?.skipped, userData?.transactions?.length, userData?.plans?.reminders, userData?.notification_preferences?.month_end_review]);

  // Initialize state from userData
  const rules = userData?.rules || [];
  // Contributions Planner is a Canada-only feature. Until the one-time seed
  // has run (plans.contributionsSeeded), fall back to the credit score setting
  // so existing users see no change. After seeding the two are independent.
  const plansSettings = userData?.plans || {};
  const contributionsEnabled = plansSettings.contributionsSeeded
    ? plansSettings.contributionsEnabled !== false
    : (userData?.credit_score?.enabled !== false);
  const transactions = userData?.transactions || [];
  const budgetSettings = userData?.budget_settings || { monthly_budget: 0, total_money: 0, categories: [] };
  const categoryBudgets = userData?.category_budgets_by_month || {};
  const savedItems = userData?.saved_items || [];
  const wishlistItems = userData?.wishlist_items || [];
  const netWorthData = userData?.net_worth_data || [];
  const sinkingFunds = userData?.sinking_funds || [];
  const extraPayments = userData?.extra_payments || [];
  const investmentAccounts = userData?.investment_accounts || [];
  const userAssets = userData?.assets || [];
  const userDebts = userData?.debts || [];
  const noSpendDays = userData?.no_spend_days || {};
  const spendDays = userData?.spend_days || {};
  const spendNotes = userData?.spend_notes || {};
  const noSpendNotes = userData?.no_spend_notes || {};
  const neutralDays = userData?.neutral_days || {};
  const neutralNotes = userData?.neutral_notes || {};
  const monthlyTargets = userData?.monthly_targets || {};
  const displayName = userData?.display_name || user?.name || 'Your';
  const profilePicture = userData?.profile_picture || null;
  
  // Get possessive form of name
  const possessiveDisplayName = displayName.trim().toLowerCase().endsWith('s') 
    ? `${displayName}'` 
    : `${displayName}'s`;

  const totalSaved = savedItems.reduce((sum, item) => sum + (item.amount || item.value || 0), 0);
  
  // Calculate current net worth matching NetWorthTab calculations
  const currentMonthKey = getMonthKey(new Date());
  
  // Calculate sinking fund balance from transactions (same as NetWorthTab)
  const getFundBalanceForDashboard = (fundId) => {
    const fund = (sinkingFunds || []).find(f => f.id === fundId);
    const startingBalance = fund?.starting_balance || 0;
    
    const deposits = (transactions || [])
      .filter(t => t.account_id === fundId && t.account_type === 'fund' && t.transaction_type === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const withdrawals = (transactions || [])
      .filter(t => t.account_id === fundId && t.account_type === 'fund' && t.transaction_type === 'withdrawal')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const interest = (transactions || [])
      .filter(t => t.interest_account_id === fundId && t.interest_account_type === 'fund' && t.is_interest)
      .reduce((sum, t) => sum + t.amount, 0);
    
    return startingBalance + deposits + interest - withdrawals;
  };
  
  // Calculate investment balance from transactions (same as NetWorthTab)
  const getInvestmentBalanceForDashboard = (invId) => {
    const inv = (investmentAccounts || []).find(i => i.id === invId);
    return investmentBalance(inv, transactions || [], currentMonthKey, { all: true }); // dark_13
  };
  
  // Calculate cash available from budget (same as BudgetTab)
  // Formula: (leftover from last month + total income excluding interest + savings withdrawals) - total spent - savings deposits
  const currentMonthTransactions = (transactions || []).filter(t => t.month_key === currentMonthKey);
  const expenseTransactionsForMonth = currentMonthTransactions.filter(t => !t.is_income && !t.is_savings);
  // Exclude interest income - it goes directly to savings/investment accounts
  const incomeTransactionsForMonth = currentMonthTransactions.filter(t => t.is_income && !t.is_from_savings && !t.is_interest);
  const savingsTransactionsForMonth = currentMonthTransactions.filter(t => t.is_savings);
  const totalSpentForMonth = expenseTransactionsForMonth.reduce((sum, t) => sum + t.amount, 0);
  const totalIncomeForMonth = incomeTransactionsForMonth.reduce((sum, t) => sum + t.amount, 0);
  // Separate deposits from withdrawals in savings transactions
  const totalSavingsDepositsForMonth = savingsTransactionsForMonth
    .filter(t => t.transaction_type !== 'withdrawal')
    .reduce((sum, t) => sum + t.amount, 0);
  const totalSavingsWithdrawalsForMonth = savingsTransactionsForMonth
    .filter(t => t.transaction_type === 'withdrawal')
    .reduce((sum, t) => sum + t.amount, 0);
  const fromSavingsTransactionsForMonth = currentMonthTransactions.filter(t => t.is_from_savings);
  const totalFromSavingsForMonth = fromSavingsTransactionsForMonth.reduce((sum, t) => sum + t.amount, 0);
  const netSavingsForMonth = totalSavingsDepositsForMonth - totalSavingsWithdrawalsForMonth - totalFromSavingsForMonth;
  const leftoverFromLastMonth = budgetSettings?.leftover_by_month?.[currentMonthKey] || 0;
  // Cash Available = Leftover + Income (excluding interest) + Withdrawals from Savings - Spent - Deposits to Savings
  const cashAvailable = (leftoverFromLastMonth + totalIncomeForMonth + totalSavingsWithdrawalsForMonth) - totalSpentForMonth - totalSavingsDepositsForMonth;
  
  // Assets and debts still use current_by_month
  const getMonthlyTotal = (items) => (items || []).reduce((sum, item) => {
    // Exact entry for current month
    if (item.current_by_month?.[currentMonthKey] !== undefined) {
      return sum + item.current_by_month[currentMonthKey];
    }
    // Carry forward from most recent entry up to current month
    const entries = Object.keys(item.current_by_month || {}).sort();
    const carryable = entries.filter(k => k <= currentMonthKey);
    if (carryable.length > 0) {
      return sum + item.current_by_month[carryable[carryable.length - 1]];
    }
    // Fall back to starting balance
    return sum + (item.starting_balance || 0);
  }, 0);
  
  // Calculate debt balance including payments
  const getDebtBalanceForDashboard = (debt) => {
    // Prefer starting_balance field, fall back to current_by_month for legacy
    let startingBalance;
    if (debt.starting_balance !== undefined) {
      startingBalance = debt.starting_balance;
    } else {
      const monthKeys = Object.keys(debt.current_by_month || {}).sort();
      startingBalance = monthKeys.length > 0 
        ? debt.current_by_month[monthKeys[monthKeys.length - 1]] 
        : (debt.original_amount || 0);
    }
    const totalPayments = (transactions || [])
      .filter(t => t.is_debt_payment && t.debt_id === debt.id)
      .reduce((sum, t) => sum + t.amount, 0);
    return Math.max(0, startingBalance - totalPayments);
  };
  
  // Only funds switched on under "Count in net worth" (fund page). Home, the
  // insight cards and the My Finances widgets all read from this total.
  const netWorthFunds = getNetWorthFunds(sinkingFunds);
  const totalSinkingFunds = netWorthFunds.reduce((sum, f) => sum + getFundBalanceForDashboard(f.id), 0);
  // plan_13: funds a money timeline step can be linked to, with balances.
  // plan_14: funds and investment accounts a money timeline step can be linked to.
  const timelineFunds = [
    ...(sinkingFunds || []).map((f) => ({ id: f.id, kind: 'fund', name: f.name, emoji: f.emoji, goal: Number(f.goal) || 0, balance: getFundBalanceForDashboard(f.id) })),
    ...(investmentAccounts || []).map((a) => ({ id: 'inv:' + a.id, kind: 'investment', name: a.name, emoji: '\u{1F4C8}', goal: Number(a.goal) || 0, balance: getInvestmentBalanceForDashboard(a.id) })),
  ];
  const totalInvestments = (investmentAccounts || []).reduce((sum, inv) => sum + getInvestmentBalanceForDashboard(inv.id), 0);
  const totalAssets = getMonthlyTotal(userAssets);
  const totalDebtsAmount = (userDebts || []).reduce((sum, d) => sum + getDebtBalanceForDashboard(d), 0);
  const currentNetWorth = totalSinkingFunds + totalInvestments + cashAvailable + totalAssets - totalDebtsAmount;
  
  // Calculate previous month's net worth — mirrors NetWorthTab logic exactly
  const previousMonth = new Date();
  previousMonth.setMonth(previousMonth.getMonth() - 1);
  const previousMonthKey = previousMonth.toISOString().slice(0, 7);
  const prevMonthCutoff = new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0, 23, 59, 59);
  const prevMonthTxByDate = (transactions || []).filter(t => new Date(t.date) <= prevMonthCutoff);
  const prevMonthTxByKey = (transactions || []).filter(t => t.month_key === previousMonthKey);

  const getPrevFundBalance = (fundId) => {
    const fund = (sinkingFunds || []).find(f => f.id === fundId);
    if (!fund) return 0;
    const startingBalance = fund.starting_balance || 0;
    const deposits = prevMonthTxByDate.filter(t => t.account_id === fundId && t.account_type === 'fund' && t.transaction_type === 'deposit').reduce((sum, t) => sum + t.amount, 0);
    const withdrawals = prevMonthTxByDate.filter(t => t.account_id === fundId && t.account_type === 'fund' && t.transaction_type === 'withdrawal').reduce((sum, t) => sum + t.amount, 0);
    const interest = prevMonthTxByDate.filter(t => t.interest_account_id === fundId && t.interest_account_type === 'fund' && t.is_interest).reduce((sum, t) => sum + t.amount, 0);
    return startingBalance + deposits + interest - withdrawals;
  };

  const getPrevInvestmentBalance = (invId) => {
    const inv = (investmentAccounts || []).find(i => i.id === invId);
    if (!inv) return 0;
    return investmentBalance(inv, transactions || [], previousMonthKey, { cutoffDay: localDay(prevMonthCutoff) }); // dark_13
  };

  const prevTotalSinkingFunds = netWorthFunds.reduce((sum, f) => sum + getPrevFundBalance(f.id), 0);
  const prevTotalInvestments = (investmentAccounts || []).reduce((sum, inv) => sum + getPrevInvestmentBalance(inv.id), 0);
  const prevTotalAssets = (userAssets || []).reduce((sum, a) => {
    if (a.current_by_month?.[previousMonthKey] !== undefined) {
      return sum + a.current_by_month[previousMonthKey];
    }
    const entries = Object.keys(a.current_by_month || {}).sort();
    const carryable = entries.filter(k => k <= previousMonthKey);
    return sum + (carryable.length > 0 ? a.current_by_month[carryable[carryable.length - 1]] : 0);
  }, 0);

  // Debt balance for previous month (same logic as NetWorthTab getDebtBalance)
  const prevTotalDebts = (userDebts || []).reduce((sum, d) => {
    let startingBalance;
    if (d.starting_balance !== undefined) {
      startingBalance = d.starting_balance;
    } else {
      const monthKeys = Object.keys(d.current_by_month || {}).filter(k => k <= previousMonthKey).sort();
      startingBalance = monthKeys.length > 0
        ? d.current_by_month[monthKeys[monthKeys.length - 1]]
        : (d.original_amount || 0);
    }
    const payments = prevMonthTxByDate
      .filter(t => t.is_debt_payment && t.debt_id === d.id)
      .reduce((s, t) => s + t.amount, 0);
    return sum + Math.max(0, startingBalance - payments);
  }, 0);

  // Cash available for previous month (same flags as NetWorthTab)
  const prevExpenses = prevMonthTxByKey.filter(t => !t.is_income && !t.is_savings).reduce((sum, t) => sum + t.amount, 0);
  const prevIncome = prevMonthTxByKey.filter(t => t.is_income && !t.is_from_savings && !t.is_interest).reduce((sum, t) => sum + t.amount, 0);
  const prevSavingsDeposits = prevMonthTxByKey.filter(t => t.is_savings && t.transaction_type !== 'withdrawal').reduce((sum, t) => sum + t.amount, 0);
  const prevSavingsWithdrawals = prevMonthTxByKey.filter(t => t.is_savings && t.transaction_type === 'withdrawal').reduce((sum, t) => sum + t.amount, 0);
  const prevLeftover = budgetSettings?.leftover_by_month?.[previousMonthKey] || 0;
  const prevCashAvailable = (prevLeftover + prevIncome + prevSavingsWithdrawals) - prevExpenses - prevSavingsDeposits;

  const previousNetWorth = prevTotalSinkingFunds + prevTotalInvestments + prevCashAvailable + prevTotalAssets - prevTotalDebts;
  
  // Calculate budget left percentage for HomeTab
  const monthlyBudget = budgetSettings?.budget_by_month?.[currentMonthKey] || 0;
  const budgetLeftPercent = monthlyBudget > 0 
    ? Math.max(0, Math.round(((monthlyBudget - totalSpentForMonth) / monthlyBudget) * 100))
    : 0;
  
  // Total savings = sinking funds + investments
  const totalSavingsForHome = totalSinkingFunds + totalInvestments;
  
  // Sync widget data whenever financial calculations update
  useEffect(() => {
    if (!userData) return;
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const now = new Date();
    const netWorth = totalSinkingFunds + totalInvestments + cashAvailable + totalAssets - totalDebtsAmount;
    const savInv = totalSinkingFunds + totalInvestments;
    const assetsTotal = totalSinkingFunds + totalInvestments + cashAvailable + totalAssets;
    const bLeftPct = monthlyBudget > 0 ? Math.max(0, Math.round(((monthlyBudget - totalSpentForMonth) / monthlyBudget) * 100)) : 0;
    // Recalculate to match BudgetTab exactly (excludes transfers)
    const widgetExpenses = currentMonthTransactions.filter(t => !t.is_income && !t.is_savings && !t.is_transfer).reduce((s, t) => s + t.amount, 0);
    const widgetSavingsDeposits = currentMonthTransactions.filter(t => t.is_savings && t.transaction_type !== 'withdrawal').reduce((s, t) => s + t.amount, 0);
    const widgetSavingsWithdrawals = currentMonthTransactions.filter(t => t.is_savings && t.transaction_type === 'withdrawal').reduce((s, t) => s + t.amount, 0);
    const widgetFromSavings = currentMonthTransactions.filter(t => t.is_from_savings).reduce((s, t) => s + t.amount, 0);
    const widgetNetSavings = widgetSavingsDeposits - widgetSavingsWithdrawals - widgetFromSavings;
    const widgetIncome = currentMonthTransactions.filter(t => t.is_income && !t.is_from_savings && !t.is_transfer).reduce((s, t) => s + t.amount, 0);
    const widgetBudgetAvail = monthlyBudget - widgetExpenses - widgetNetSavings;
    // Same function the Budget tab's ring uses — honours excluded categories
    const widgetSpentPct = computeBudgetSpentPercent({
      expenseTransactions: currentMonthTransactions.filter(t => !t.is_income && !t.is_savings && !t.is_transfer),
      monthlyCategoryBudgets: categoryBudgets[currentMonthKey] || {},
      monthlyDebtBudgets: (budgetSettings?.debt_budgets_by_month || {})[currentMonthKey] || {},
      userDebts,
      excludedCategories: budgetSettings?.excluded_categories || [],
      monthlyBudget,
    }).budgetSpentPercent;
    // Sinking funds widget — same functions the Funds tab renders from
    const widgetFundsData = JSON.stringify(buildFundsWidgetData({
      sinkingFunds,
      transactions,
      extraPayments,
      limit: getWidgetLimit('sinkingFunds'),
      theme: widgetThemeFor(userData, purpleWidgets),
      cutoff: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59),
    }));
    // Upcoming bills widget — same occurrence maths as the Scheduled list
    const widgetBillsData = JSON.stringify(buildBillsWidgetData({
      recurringTransactions: budgetSettings?.recurring_transactions || [],
      transactions,
      from: now,
      days: 7,
      theme: widgetThemeFor(userData, purpleWidgets),
    }));
    // No-spend calendar widget: this month's days, target and Willpower Wins total
    const calKey = getMonthKey(now);
    const calSaved = (userData?.saved_items || []).filter(i => i.date && i.date.startsWith(calKey)).reduce((s, i) => s + (Number(i.amount) || 0), 0);
    // w2_09: Worthly 2.0 widgets - this month's net worth change and the milestones
    let w2PlanGrow = null;
    try {
      if (isMoneyPlanShown() && userData?.money_diary?.plan) w2PlanGrow = planGrowth(userData.money_diary.plan, userData.money_diary || {}, userData);
    } catch (e) { w2PlanGrow = null; }
    const w2Pace = w2PlanGrow ? 0 : recentPace(transactions);
    const w2Grow = w2PlanGrow || (w2Pace > 0 ? (k) => w2Pace * k : null);
    const widgetW2Data = JSON.stringify({
      nwChange: Number.isFinite(previousNetWorth) ? netWorth - previousNetWorth : 0,
      milestones: worthMilestoneSteps(netWorth, w2Grow),
    });
    const widgetCalendarData = JSON.stringify({
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      noSpend: (userData?.no_spend_days?.[calKey] || []).map(Number),
      neutral: (userData?.neutral_days?.[calKey] || []).map(Number),
      spend: (userData?.spend_days?.[calKey] || []).map(Number),
      target: Number(userData?.monthly_targets?.[calKey]) || 15,
      saved: calSaved
    });
    updateWidgetData({
      calendarData: widgetCalendarData,
      fundsData: widgetFundsData,
      billsData: widgetBillsData,
      w2Data: widgetW2Data, // w2_09
      firstName: (userData?.display_name || 'there').split(' ')[0],
      netWorth: netWorth,
      assets: assetsTotal,
      savingsInvestments: savInv,
      budgetPercentLeft: bLeftPct,
      budgetSpentPercent: widgetSpentPct,
      totalIncome: widgetIncome,
      totalSpent: widgetExpenses,
      totalSavings: widgetSavingsDeposits,
      budgetAvailable: widgetBudgetAvail,
      currentMonth: monthNames[now.getMonth()],
      currentYear: now.getFullYear(),
      theme: widgetThemeFor(userData, purpleWidgets),
      currencySymbol: (() => {
          // Same list as the rest of the app, so every currency gets its own symbol
          return (CURRENCIES.find(c => c.code === userData?.currency) || CURRENCIES[0]).symbol;
        })()
    });
  }, [userData, totalSinkingFunds, totalInvestments, cashAvailable, totalAssets, totalDebtsAmount, totalIncomeForMonth, totalSpentForMonth, totalSavingsDepositsForMonth, monthlyBudget, currentMonthTransactions, categoryBudgets, userDebts, budgetSettings, sinkingFunds, extraPayments, transactions, purpleWidgets, previousNetWorth]);

  // Total positive (matches Net Worth hero "Assets" box)
  const totalPositiveForHome = totalSinkingFunds + totalInvestments + cashAvailable + totalAssets;

  const handleProfileUpload = async (imageData) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/profile-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${keychainService.getCached()}` },
        body: JSON.stringify({ image: imageData })
      });
      if (response.ok) {
        setAppSettings(prev => ({ ...prev, profile_image: imageData }));
      }
    } catch (error) {
      toast.error('Failed to upload profile image');
    }
  };

  return (
    <TourController
      userId={user?.user_id}
      userData={userData}
      theme={userData?.theme || 'pink'}
      rules={rules}
      transactions={transactions}
      noSpendDays={noSpendDays}
      spendDays={spendDays}
      neutralDays={neutralDays}
      wishlistItems={wishlistItems}
      setActiveTab={setActiveTab}
      isBudgetSheetOpen={isBudgetSheetOpen}
      isCalendarDialogOpen={isCalendarDialogOpen}
      openAddTransaction={() => setTourOpenBudgetAdd(true)}
      openAddRule={() => {}}
      openAddNoSpendDay={() => {}}
      openAddWishlistItem={() => {}}
      hasTrialOrPromo={true}
      onStartTrial={() => {}}
    >
    <div className="app-container safe-area-top" style={{ scrollMarginTop: 0 }}>
      {/* Fixed aurora background — viewport-locked so it never stretches with scroll */}
      <div className="bg-aurora-theme" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />


      <main className="max-w-5xl mx-auto px-0 md:px-8 pb-24 min-h-screen" style={{ position: 'relative', zIndex: 1 }}>
        {(activeTab === 'rules' || (isPlanOn() && (activeTab === 'plannotes' || activeTab === 'planreminders' || activeTab === 'planbudgets' || activeTab === 'yearlycosts' || activeTab === 'debtpayoff' || activeTab === 'futureworth'))) && (
          <HomeTab 
            timelineFunds={timelineFunds}
            onOpenMoneyPlan={() => setMoneyPlanOpen(true)}
            user={user}
            userData={userData}
            appSettings={appSettings}
            rules={rules} 
            saveUserData={saveUserData} 
            theme={userData?.theme}
            netWorth={currentNetWorth}
            totalAssets={totalPositiveForHome}
            totalSavings={totalSavingsForHome}
            budgetLeftPercent={budgetLeftPercent}
            onOpenSettings={() => setSettingsOpen(true)}
            onNavigateToTab={setActiveTab}
            onProfileUpload={handleProfileUpload}
            possessiveDisplayName={possessiveDisplayName}
            sinkingFunds={sinkingFunds}
            investmentAccounts={investmentAccounts}
            transactions={transactions}
            budgetSettings={{...budgetSettings, sinking_funds: sinkingFunds, investment_accounts: investmentAccounts}}
            categoryBudgets={categoryBudgets}
            noSpendDays={noSpendDays}
            wishlistItems={wishlistItems}
            previousNetWorth={previousNetWorth}
            netWorthBreakdown={{
              savings: totalSinkingFunds, investments: totalInvestments,
              assets: totalAssets, debts: totalDebtsAmount, cash: cashAvailable,
              prevSavings: prevTotalSinkingFunds, prevInvestments: prevTotalInvestments,
              prevAssets: prevTotalAssets, prevDebts: prevTotalDebts, prevCash: prevCashAvailable
            }}
            monthlyTargets={monthlyTargets}
            creditScore={userData?.credit_score || {}}
            contributionsEnabled={contributionsEnabled}
          />
        )}
        {/* Notes, Reminders and Budget Sheets - one component, three pages.
            The key makes each a fresh page rather than a tab switch. */}
        {(activeTab === 'plannotes' || activeTab === 'planreminders' || activeTab === 'planbudgets') && (
          <NotesLayer on={isPlanOn()}>
          <NotesTab
            key={activeTab}
            section={activeTab === 'planreminders' ? 'reminders' : activeTab === 'planbudgets' ? 'budgets' : 'notes'}
            onBack={() => setActiveTab('rules')}
            plans={userData?.plans}
            moneyTimeline={userData?.money_timeline}
            timelineFunds={timelineFunds}
            saveUserData={saveUserData}
            theme={userData?.theme || 'pink'}
            remindersEnabled={userData?.notification_preferences?.reminders !== false}
            moneyPlan={userData?.money_diary?.plan} // plan_27
            onOpenCheckup={() => { setMoneyPlanStart('checkup'); setMoneyPlanOpen(true); }}
          />
          </NotesLayer>
        )}
        {activeTab === 'plans' && contributionsEnabled && (
          <PlansTab
            onBack={() => {
              // The planner lives on Wealth > Invest now - go back there.
              try { localStorage.setItem('worthly_wealth_view', 'investments'); } catch (e) { /* private mode */ }
              setActiveTab('savings');
            }}
            plans={userData?.plans}
            saveUserData={saveUserData}
            theme={userData?.theme || 'pink'}
            investmentAccounts={investmentAccounts}
            transactions={transactions}
          />
        )}
        {/* w2_16: yearly costs, on top of Home in Worthly 2.0 */}
        {activeTab === 'yearlycosts' && isPlanOn() && (
          <YearlyCosts
            costs={userData?.yearly_costs || []}
            funds={timelineFunds.filter((f) => f.kind === 'fund')}
            onSave={(list) => saveUserData({ yearly_costs: list })}
            onBack={() => setActiveTab('rules')}
          />
        )}
        {/* w2_17: debt payoff, on top of Home in Worthly 2.0 */}
        {activeTab === 'debtpayoff' && isPlanOn() && (
          <DebtPayoff
            data={userData?.debt_payoff}
            wealthDebts={(userDebts || []).map((d) => ({ id: d.id, name: d.name, emoji: d.emoji }))}
            onSave={(next) => saveUserData({ debt_payoff: next })}
            onBack={() => setActiveTab('rules')}
          />
        )}
        {activeTab === 'futureworth' && (
          <SwipeLayer on={isPlanOn()} onBack={() => setActiveTab('rules')}>
          <FutureWorthTab
            onBack={() => setActiveTab('rules')}
            futureWorth={userData?.future_worth}
            saveUserData={saveUserData}
            theme={userData?.theme || 'pink'}
          />
          </SwipeLayer>
        )}
        {/* mp_01: money plan trial - opens on top of Home, only with the Money plan theme on */}
        {moneyPlanOpen && isPlanOn() && (
          <MoneyPlanPage
            userData={userData}
            onSave={(plan) => saveUserData({ 'money_diary.plan': plan })}
            onBack={() => { setMoneyPlanOpen(false); setMoneyPlanStart(null); }}
            refresh={loadUserData}
            startPage={moneyPlanStart} // plan_27
          />
        )}
        {activeTab === 'calendar' && (
          <CalendarTab 
            noSpendDays={noSpendDays}
            spendDays={spendDays}
            spendNotes={spendNotes}
            noSpendNotes={noSpendNotes}
            neutralDays={neutralDays}
            neutralNotes={neutralNotes}
            monthlyTargets={monthlyTargets}
            totalSaved={totalSaved}
            savedItems={savedItems}
            saveUserData={saveUserData}
            theme={userData?.theme || 'pink'}
            onShowHelp={() => showTutorialForTab('calendar')}
          />
        )}
        {activeTab === 'budget' && (
          <BudgetTab
            key={JSON.stringify(userData?.cards || [])}
            transactions={transactions}
            budgetSettings={{...budgetSettings, sinking_funds: sinkingFunds, investment_accounts: investmentAccounts, recurring_transactions: userData?.recurring_transactions}}
            categoryBudgets={categoryBudgets}
            saveUserData={saveUserData}
            userCards={userData?.cards}
            userDebts={userDebts}
            theme={userData?.theme || 'pink'}
            noSpendDays={noSpendDays}
            userData={userData}
            onShowHelp={() => showTutorialForTab('budget')}
            openAddMenu={widgetOpenAddMenu || tourOpenBudgetAdd}
            onOpenAddMenuConsumed={() => { setWidgetOpenAddMenu(false); setTourOpenBudgetAdd(false); }}
          />
        )}
        {activeTab === 'savings' && (
          <SinkingFundsTab previousNetWorth={previousNetWorth} 
            sinkingFunds={sinkingFunds} 
            extraPayments={extraPayments} 
            transactions={transactions}
            investmentAccounts={investmentAccounts}
            userAssets={userAssets}
            userDebts={userDebts}
            budgetSettings={budgetSettings}
            saveUserData={saveUserData}
            theme={userData?.theme || 'pink'}
            onShowHelp={() => showTutorialForTab('wealth')}
            plans={userData?.plans}
            contributionsEnabled={contributionsEnabled}
            onOpenPlanner={() => setActiveTab('plans')}
            userData={userData} // plan_22
          />
        )}
        {activeTab === 'wishlist' && (
          <WishlistTab wishlistItems={wishlistItems} saveUserData={saveUserData} userData={userData} theme={userData?.theme} onShowHelp={() => showTutorialForTab('wishlist')} />
        )}
      </main>

      <Worthly2Intro userData={userData} saveUserData={saveUserData} onTried={() => setActiveTab('rules')} />{/* w2_02 */}
      <SiriHowToHost theme={userData?.theme || 'pink'} onBackToSettings={() => setSettingsOpen(true)} />{/* reorder_02 */}
      {/* Settings Dialog - Lazy loaded */}
      {settingsOpen && (
        <Suspense fallback={null}>
          <SettingsDialog
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
            onLogout={logout}
            onResetAccount={() => {
              // Reset local state and reload to trigger fresh onboarding
              setUserData(null);
              window.location.reload();
            }}
            userData={userData}
            user={user}
            saveUserData={saveUserData}
            onViewTutorial={() => setShowTutorial(true)}
            onViewPremiumFeatures={() => setShowPremiumFeatures(true)}
            widgetData={{ netWorth: currentNetWorth, totalAssets: totalPositiveForHome, totalSavings: totalSavingsForHome, budgetLeftPercent }}
            onWidgetSync={() => {
              const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              const now = new Date();
              updateWidgetData({
                firstName: (displayName || 'there').split(' ')[0],
                netWorth: currentNetWorth,
                assets: totalPositiveForHome,
                savingsInvestments: totalSavingsForHome,
                budgetPercentLeft: budgetLeftPercent,
                budgetSpentPercent: computeBudgetSpentPercent({
                  expenseTransactions: currentMonthTransactions.filter(t => !t.is_income && !t.is_savings && !t.is_transfer),
                  monthlyCategoryBudgets: categoryBudgets[currentMonthKey] || {},
                  monthlyDebtBudgets: (budgetSettings?.debt_budgets_by_month || {})[currentMonthKey] || {},
                  userDebts,
                  excludedCategories: budgetSettings?.excluded_categories || [],
                  monthlyBudget,
                }).budgetSpentPercent,
                totalIncome: totalIncomeForMonth,
                totalSpent: totalSpentForMonth,
                totalSavings: totalSavingsDepositsForMonth,
                budgetAvailable: monthlyBudget - totalSpentForMonth - netSavingsForMonth,
                currentMonth: monthNames[now.getMonth()],
                currentYear: now.getFullYear(),
                theme: widgetThemeFor(userData, purpleWidgets),
                currencySymbol: (() => {
          // Same list as the rest of the app, so every currency gets its own symbol
          return (CURRENCIES.find(c => c.code === userData?.currency) || CURRENCIES[0]).symbol;
        })()
              });
            }}
          />
        </Suspense>
      )}

      {/* Tutorial Overlay */}
      {showTutorial && (
        <div className="fixed inset-0 z-50">
          <WelcomeTutorial 
            saveUserData={saveUserData} 
            userName={userData?.display_name} // w2_05
            aboveTabs // w2_06: buttons above the tab bar
            onComplete={() => {
              setShowTutorial(false);
              setTutorialStartSlide(0);
            }}
            showBlurredBackground={false}
            startSlide={tutorialStartSlide}
          />
        </div>
      )}

      {/* Premium Features Modal */}
      {showPremiumFeatures && (
        <PremiumFeaturesModal 
          open={showPremiumFeatures} 
          onClose={() => setShowPremiumFeatures(false)} 
        />
      )}

      {/* Fixed Bottom Tab Navigation */}
      <TabNav activeTab={activeTab} setActiveTab={setActiveTab} theme={userData?.theme || "pink"} />

      {/* Review ask - full screen */}
      {showReviewFallback && (
        <ReviewAskScreen
          theme={userData?.theme || 'pink'}
          onRate={async () => {
            await reviewService.openAppStoreForReview();
            setShowReviewFallback(false);
          }}
          onNotNow={() => {
            reviewService.markUserDeclined();
            setShowReviewFallback(false);
          }}
        />
      )}

      {/* New version pop-up */}
      {showUpdatePrompt && (
        <UpdatePromptSheet
          theme={userData?.theme || 'pink'}
          onUpdate={() => {
            setShowUpdatePrompt(false);
            openAppStorePage();
          }}
          onLater={() => setShowUpdatePrompt(false)}
        />
      )}
    </div>
    </TourController>
  );
};

// ============== Rules Tab ==============
// Sortable Rule Item Component

// ============== Rules Tab Components ==============
// SortableRuleItem, RulesTab, and QuizBox have been extracted to:
// - /components/tabs/RulesTab.jsx (includes SortableRuleItem)
// - /components/common/QuizBox.jsx


// ============== Sinking Funds Tab ==============
// Note: DEFAULT_SINKING_FUNDS is now imported from utils/constants.js


// ============== Sinking Funds Tab (Wealth) ==============
// SinkingFundsTab has been extracted to: /components/tabs/SinkingFundsTab.jsx


// ============== Wishlist Tab ==============

// ============== Wishlist Tab ==============
// WishlistTab has been extracted to: /components/tabs/WishlistTab.jsx


// ============== Net Worth Tab ==============
// Note: DEFAULT_INVESTMENT_ACCOUNTS is now imported from utils/constants.js


// ============== Net Worth Tab ==============
// NetWorthTab has been extracted to: /components/tabs/NetWorthTab.jsx


// ============== Calendar Tab ==============

// ============== Calendar Tab ==============
// CalendarTab has been extracted to: /components/tabs/CalendarTab.jsx


// ============== Budget Tab ==============
// ============== Transaction Row Component ==============
const TransactionRow = ({ 
  transaction, 
  showDate, 
  budgetSettings, 
  editingTransaction, 
  editTransactionData, 
  setEditTransactionData,
  editCategorySearch,
  setEditCategorySearch,
  showEditCategoryDropdown,
  setShowEditCategoryDropdown,
  editFilteredCategories,
  selectEditCategory,
  saveEditTransaction,
  cancelEditTransaction,
  startEditTransaction,
  removeTransaction,
  toggleTransactionCompleted,
  setViewingTransaction,
  getCardColors,
  getAccountName,
  formatCurrency,
  colorMap,
  cards,
  theme = 'pink'
}) => {
  const isEditing = editingTransaction === transaction.id;
  
  // Hex color map for inline styles
  const hexColorMap = {
    grey: { bg: '#9FA2AC', bgLight: '#E4E3E9', text: '#6B6B78' },
    red: { bg: '#E6827D', bgLight: '#F6C8C4', text: '#C4443F' },
    blue: { bg: '#7C9FE6', bgLight: '#C9D8F5', text: '#3F63C4' },
    orange: { bg: '#F0A06C', bgLight: '#FAD4B8', text: '#B8683F' },
    green: { bg: '#6EC08F', bgLight: '#C8E8D3', text: '#3F8A5F' },
    pink: { bg: '#E27FAB', bgLight: '#F7CCDE', text: '#C23A72' },
    purple: { bg: '#A488E0', bgLight: '#DDD1F2', text: '#7A5FB0' }
  };
  
  // Get the card color for this transaction (if it's an expense with a card)
  const getTransactionCardColor = () => {
    if (transaction.is_income || transaction.is_savings) return null;
    // Try card_id first, then payment_method
    if (transaction.card_id) {
      const card = cards?.find(c => c.id === transaction.card_id);
      return card?.color || null;
    }
    if (transaction.payment_method) {
      const card = cards?.find(c => c.name === transaction.payment_method);
      return card?.color || null;
    }
    return null;
  };
  
  const cardColor = getTransactionCardColor();
  const cardHexColors = cardColor ? hexColorMap[cardColor] : null;
  
  return (
    <div 
      className="px-4 py-2 hover:theme-bg-50/50 transition-colors group cursor-pointer"
      onClick={() => !isEditing && setViewingTransaction(transaction)}
    >
      {isEditing ? (
        // Edit mode - expanded with all fields
        <div className="space-y-2 py-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2">
            {/* Amount */}
            <div className="w-24">
              <Input 
                type="number" 
                value={editTransactionData.amount} 
                onChange={(e) => setEditTransactionData(prev => ({ ...prev, amount: e.target.value }))} 
                placeholder="Amount" 
                className="h-8 text-sm theme-border-200" 
              />
            </div>
            
            {/* Category (for expenses only) */}
            {!transaction.is_income && !transaction.is_savings && (
              <div className="relative flex-1">
                <Input
                  type="text"
                  value={editCategorySearch || editTransactionData.category}
                  onChange={(e) => {
                    setEditCategorySearch(e.target.value);
                    setEditTransactionData(prev => ({ ...prev, category: e.target.value }));
                    setShowEditCategoryDropdown(true);
                  }}
                  onFocus={() => setShowEditCategoryDropdown(true)}
                  placeholder="Category"
                  className="h-8 text-sm theme-border-200"
                />
                {showEditCategoryDropdown && editFilteredCategories.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border theme-border-200 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                    {editFilteredCategories.slice(0, 5).map(cat => (
                      <button
                        key={cat}
                        onClick={() => selectEditCategory(cat)}
                        className="w-full px-3 py-1.5 text-left text-sm hover:theme-bg-50"
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Description/Note */}
            <Input 
              type="text" 
              value={editTransactionData.description} 
              onChange={(e) => setEditTransactionData(prev => ({ ...prev, description: e.target.value }))} 
              placeholder="Note (optional)" 
              className="flex-1 h-8 text-sm theme-border-200" 
            />
            
            {/* Save/Cancel buttons */}
            <Button onClick={(e) => { e.stopPropagation(); saveEditTransaction(); }} size="sm" className="h-8 theme-bg-500 hover:theme-bg-600 text-white px-3">
              <Check className="w-3 h-3 mr-1" /> Save
            </Button>
            <Button onClick={(e) => { e.stopPropagation(); cancelEditTransaction(); }} variant="outline" size="sm" className="h-8 theme-border-200 px-3">
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        // View mode - compact
        <div className={`flex items-center gap-2 ${budgetSettings.show_checkmarks && transaction.completed ? 'opacity-50' : ''}`}>
          {/* Checkbox */}
          {budgetSettings.show_checkmarks && (
            <button
              onClick={(e) => { e.stopPropagation(); toggleTransactionCompleted(transaction.id); }}
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                transaction.completed 
                  ? 'bg-gray-400 border-gray-400' 
                  : transaction.is_income 
                    ? (transaction.is_interest ? 'border-blue-300' : 'border-emerald-300')
                    : transaction.is_savings
                      ? (transaction.transaction_type === 'withdrawal' ? 'border-amber-300' : 'border-purple-300')
                      : 'theme-border-300'
              }`}
            >
              {transaction.completed && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </button>
          )}
          
          {/* Icon - Use card color for expenses, purple for deposits, amber for withdrawals */}
          <div 
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              transaction.completed ? 'bg-gray-100' : ''
            }`}
            style={!transaction.completed ? {
              backgroundColor: transaction.is_income 
                ? '#d1fae5' // light green for income
                : transaction.is_savings
                  ? (transaction.transaction_type === 'withdrawal' ? '#fef3c7' : '#f3e8ff') // amber-100 for withdrawal, purple-100 for deposit
                  : (cardHexColors?.bgLight || '#f3f4f6')
            } : undefined}
          >
            {transaction.is_income ? (
              <TrendingUp 
                className={`w-3.5 h-3.5 ${transaction.completed ? 'text-gray-400' : ''}`}
                style={!transaction.completed ? { color: '#34d399' } : undefined}
              />
            ) : transaction.is_savings ? (
              transaction.transaction_type === 'withdrawal' 
                ? <Wallet className={`w-3.5 h-3.5 ${transaction.completed ? 'text-gray-400' : 'text-amber-500'}`} />
                : <PiggyBank className={`w-3.5 h-3.5 ${transaction.completed ? 'text-gray-400' : 'text-purple-500'}`} />
            ) : (
              <Tag 
                className={`w-3.5 h-3.5 ${transaction.completed ? 'text-gray-400' : ''}`}
                style={!transaction.completed && cardHexColors ? { color: cardHexColors.text } : (!transaction.completed ? { color: '#6b7280' } : undefined)}
              />
            )}
          </div>
          
          {/* Details */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className={`text-sm font-medium truncate ${
                transaction.completed 
                  ? 'text-gray-400 line-through' 
                  : 'text-gray-700'
              }`}>
                {transaction.is_income 
                  ? (transaction.description || (transaction.is_interest ? 'Interest' : 'Income')) 
                  : transaction.is_savings 
                    ? (transaction.transaction_type === 'withdrawal' ? 'Withdrawal' : (transaction.description || 'Deposit')) 
                    : transaction.category}
              </p>
              {!transaction.is_income && !transaction.is_savings && transaction.payment_method && (
                <span 
                  className="text-[10px] px-1.5 py-0.5 rounded-full"
                  style={cardHexColors ? {
                    backgroundColor: cardHexColors.bgLight,
                    color: cardHexColors.text
                  } : {
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280'
                  }}
                >
                  {transaction.payment_method}
                </span>
              )}
              {transaction.is_income && transaction.to_card && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600">
                  → {transaction.to_card}
                </span>
              )}
              {transaction.is_recurring && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 flex items-center gap-0.5">
                  <RefreshCw className="w-2.5 h-2.5" />
                  Recurring
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              {transaction.description && !transaction.is_income && !transaction.is_savings && (
                <span className="truncate max-w-[120px]">{transaction.description}</span>
              )}
              {(transaction.is_savings || transaction.is_interest) && (transaction.account_id || transaction.interest_account_id) && (
                <span className="truncate text-gray-400">
                  → {getAccountName(transaction.is_interest ? transaction.interest_account_id : transaction.account_id, transaction.is_interest ? transaction.interest_account_type : transaction.account_type)}
                </span>
              )}
              {showDate && (
                <span>{new Date(transaction.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              )}
            </div>
          </div>
          
          {/* Amount */}
          <span className={`text-sm font-bold text-right min-w-[70px] flex-shrink-0 ${
            transaction.completed 
              ? 'text-gray-400 line-through' 
              : transaction.is_income 
                ? 'text-[#34d399]'
                : 'text-gray-700'
          }`}>
            {transaction.is_income ? '+' : '-'}{formatCurrency(transaction.amount)}
          </span>
        </div>
      )}
    </div>
  );
};


// ============== Budget Tab ==============
// BudgetTab has been extracted to: /components/tabs/BudgetTab.jsx


// ============== Notification Prompt ==============
// Shows after onboarding. Uses context to auto-dismiss if already prompted.
const NotificationPrompt = ({ theme }) => {
  const { shouldShowPermissionPrompt, requestPermission, dismissPrompt } = useNotifications();
  const [showModal, setShowModal] = useState(false);

  // Tour-active gate: re-evaluates when the tour toggles.
  const [tourActiveForNotif, setTourActiveForNotif] = useState(
    typeof window !== 'undefined' && !!window.__worthlyTourActive
  );
  useEffect(() => {
    const h = (e) => setTourActiveForNotif(!!e?.detail?.active);
    window.addEventListener('worthly:tour-active-change', h);
    return () => window.removeEventListener('worthly:tour-active-change', h);
  }, []);

  useEffect(() => {
    if (!shouldShowPermissionPrompt()) return;
    if (tourActiveForNotif) return; // don't start any timer while the tour is on

    // Wait for the user to decide on premium (either dismiss or buy),
    // then start the 60s timer. Works for both new and existing users:
    // the event also fires when existing users dismiss the paywall.
    let timer;
    const onDecided = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setShowModal(true), 60000);
    };
    window.addEventListener('worthly:trial-prompt-decided', onDecided);

    // Existing-user fallback: if the paywall was already dismissed in a prior
    // session (trial_prompt_seen_* is set) AND the tour isn't active, start
    // the timer immediately on mount.
    let alreadyDecided = false;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('trial_prompt_seen_')) {
          alreadyDecided = true;
          break;
        }
      }
    } catch (e) {}
    if (alreadyDecided) {
      timer = setTimeout(() => setShowModal(true), 60000);
    }

    return () => {
      window.removeEventListener('worthly:trial-prompt-decided', onDecided);
      if (timer) clearTimeout(timer);
    };
  }, [shouldShowPermissionPrompt, tourActiveForNotif]);

  // Don't show if a dialog (e.g. Settings) is open - focus trap would block interaction
  // Re-check every second until dialog closes
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => {
    if (!showModal) return;
    const check = setInterval(() => {
      setDialogOpen(document.querySelector('[role="dialog"]') !== null);
    }, 1000);
    return () => clearInterval(check);
  }, [showModal]);

  if (!showModal || dialogOpen) return null;

  return (
    <NotificationPermissionModal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      onEnable={requestPermission}
      onSkip={() => { dismissPrompt(); setShowModal(false); }}
      theme={theme || 'pink'}
    />
  );
};

// ============== App Router ==============
function AppRouter() {
  const location = useLocation();

  // Listen for deep link from iOS OAuth callback
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      CapApp.addListener('appUrlOpen', async (event) => {
        // Handle OAuth callback
        const match = event.url.match(/session_token=([^&]+)/);
        if (match && match[1]) {
          const token = match[1];
          Browser.close();
          await keychainService.setItem('session_token', token);
          window.location.href = '/dashboard';
          return;
        }
        // Handle widget deep links
        if (event.url.includes('://budget')) {
          localStorage.setItem('widget_open_tab', 'budget');
          if (event.url.includes('action=addTransaction')) {
            localStorage.setItem('widget_budget_action', 'addTransaction');
            window.dispatchEvent(new CustomEvent('widget_add_transaction'));
          }
          window.location.replace('/dashboard');
        } else if (event.url.includes('://calendar')) {
          localStorage.setItem('widget_open_tab', 'calendar');
          window.location.href = '/dashboard';
        } else if (event.url.includes('://home')) {
          localStorage.setItem('widget_open_tab', 'rules');
          window.location.href = '/dashboard';
        }
      });
    }
  }, []);

  // Check for session_id in URL hash (synchronously during render)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Check for OAuth code in query string (Google redirect)
  if (location.pathname === '/auth/callback' || location.search?.includes('code=')) {
    return <AuthCallback />;
  }

  // Public routes - accessible without login
  if (location.pathname === '/privacy') {
    return <PrivacyPolicy />;
  }
  if (location.pathname === '/terms') {
    return <TermsOfService />;
  }

  return (
    <AuthContext>
      {({ user, userData, appSettings, login, handleAppleSignIn, logout, saveUserData, loadUserData, loadAppSettings, setAppSettings }) => {
        if (!user) {
          return <LoginPage login={login} handleAppleSignIn={handleAppleSignIn} />;
        }
        
        if (!userData) {
          return <LoadingScreen title="Setting up your finances..." subtitle="Organizing your budget" />;
        }























        // Check if user needs to set their display name
        if (!userData.display_name) {
          const handleSaveName = async (name, picture) => {
            // w2_01: everyone new starts on Worthly 2.0, and it's their only look.
            const updateData = { display_name: name, theme: 'pink', worthly2: true, worthly2_only: true };
            if (picture) {
              updateData.profile_picture = picture;
            }
            await saveUserData(updateData);
            // Reload user data to get the updated name
            await loadUserData();
          };
          return <NameEntryPage onSaveName={handleSaveName} user={user} />;
        }

        // Check if user needs to complete onboarding
        if (!userData.onboarding_complete) {
          const handleOnboardingComplete = async () => {
            // Reload user data after onboarding
            await loadUserData();
          };
          return <OnboardingFlow saveUserData={saveUserData} onComplete={handleOnboardingComplete} userName={userData?.display_name} />;
        }

        // Check if user needs to see the welcome tutorial (only for users who just finished onboarding)
        // tutorial_completed === false means they just finished onboarding
        // tutorial_completed === undefined means they're an existing user (skip tutorial)
        if (userData.tutorial_completed === false) {
          return <WelcomeTutorial saveUserData={saveUserData} onComplete={() => {}} userName={userData?.display_name} firstTime />; // w2_05
        }

        return (
          <NotificationProvider userId={user.user_id} userData={userData} saveUserData={saveUserData}>
            <SubscriptionProvider userData={userData} apiUrl={API_URL} theme={userData?.theme}>
              <NotificationPrompt theme={userData?.theme} />
              <SubscriptionAwareRoutes 
                user={user} 
                userData={userData} 
                appSettings={appSettings} 
                logout={logout} 
                saveUserData={saveUserData} 
                loadAppSettings={loadAppSettings} 
                setAppSettings={setAppSettings}
                loadUserData={loadUserData}
              />
            </SubscriptionProvider>
          </NotificationProvider>
        );
      }}
    </AuthContext>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster
        position="top-center"
        duration={3000}
        offset="env(safe-area-inset-top, 0px)"
        toastOptions={{
          style: {
            background: 'rgba(255, 255, 255, 0.72)',
            WebkitBackdropFilter: 'saturate(180%) blur(20px)',
            backdropFilter: 'saturate(180%) blur(20px)',
            border: 'none',
            borderRadius: '16px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.06)',
            marginTop: 'max(env(safe-area-inset-top, 0px), 12px)',
          },
        }}
      />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
